import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronDown, Trash2 } from 'lucide-react';

import { useDesignContext } from '../../hooks/useDesignContext';
import { buildApiUrl } from '../../config/api';
import { adjustLastNameCasing } from '../../utils/nameCasing';
import { OrderExtract, ExtractedPerson, ExtractionMeta } from './OrderExtract';

// Simple primary OK button (clean, no animation)
const AnimatedOkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  background: #5D722C;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #4a5b23;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(93, 114, 44, 0.35);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 8px rgba(93, 114, 44, 0.28);
  }
`;

// --- Text Persistence Utilities ---

export const extractPersonalFormDataFromElements = (elements: any[]) => {
  const getText = (id: string) => elements.find((e: any) => e.type === 'text' && e.id === id)?.properties?.content || '';

  const first = getText('firstName');
  const middle = getText('middleName');
  const last = getText('lastName');
  const dob = getText('dob');
  const dod = getText('dod');
  const verseContent = getText('verse');
  const weddingDateContent = getText('weddingDate');
  const additionalText = getText('additionalText');

  const p2_first = getText('p2_firstName');
  const p2_middle = getText('p2_middleName');
  const p2_dob_val = getText('p2_dob');
  const p2_dod_val = getText('p2_dod');

  const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

  const parseD = (str: string) => {
    if (!str || str.toLowerCase().includes('undefined')) return { m: 'MONTH', d: 'DD', y: 'YYYY' };
    const typed = parseTypedDate(str);
    if (typed) {
      return { m: typed.month, d: typed.day, y: typed.year };
    }
    const s = str.replace(/[.,\/]/g, ' ').toUpperCase();
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1 && /^\d{4}$/.test(parts[0])) {
      return { m: 'MONTH', d: 'DD', y: parts[0] };
    }
    if (parts.length < 3) return { m: 'MONTH', d: 'DD', y: 'YYYY' };
    const foundM = MONTHS.find(m => m.startsWith(parts[0]) || parts[0].startsWith(m));
    return { m: foundM || 'MONTH', d: parts[1], y: parts[2] };
  };

  const dobObj = parseD(dob);
  const dodObj = parseD(dod);
  const p2_dobObj = parseD(p2_dob_val);
  const p2_dodObj = parseD(p2_dod_val);
  const weddingObj = parseD(weddingDateContent);

  // Detect if any existing dates are abbreviated to set the initial checkbox state
  const isAbbreviated = (str: string) => {
    if (!str) return false;
    const firstToken = str.split(/\s+/)[0].toUpperCase().replace('.', '');
    if (!firstToken) return false;
    return !MONTHS.includes(firstToken);
  };

  const hasAbbreviation = isAbbreviated(dob) || isAbbreviated(dod) || isAbbreviated(p2_dob_val) || isAbbreviated(p2_dod_val) || isAbbreviated(weddingDateContent);

  return {
    firstName: first || '',
    middleName: middle || '',
    lastName: last || '',
    birthMonth: dobObj.m !== 'MONTH' ? dobObj.m : 'JANUARY',
    birthDay: dobObj.d !== 'DD' ? dobObj.d : '1',
    birthYear: dobObj.y !== 'YYYY' ? dobObj.y : '1940',
    deathMonth: dodObj.m,
    deathDay: dodObj.d,
    deathYear: dodObj.y,
    hideDeathDate: !dod,
    p2_firstName: p2_first || '',
    p2_middleName: p2_middle || '',
    p2_birthMonth: p2_birthMonth(p2_dobObj.m),
    p2_birthDay: p2_dobObj.d !== 'DD' ? p2_dobObj.d : '1',
    p2_birthYear: p2_dobObj.y !== 'YYYY' ? p2_dobObj.y : '1940',
    p2_deathMonth: p2_dodObj.m,
    p2_deathDay: p2_dodObj.d,
    p2_deathYear: p2_dodObj.y,
    p2_hideDeathDate: !p2_dod_val,
    weddingMonth: weddingObj.m,
    weddingDay: weddingObj.d,
    weddingYear: weddingObj.y,
    hideWeddingDate: !weddingDateContent,
    customVerse: verseContent || '',
    hideVerse: false,
    verse: verseContent || '',
    weddingDate: weddingDateContent || '',
    additionalText: additionalText || '',
    additionalText1: getText('additionalText1') || additionalText || '',
    additionalText2: getText('additionalText2') || '',
    additionalText3: getText('additionalText3') || '',
    additionalText4: getText('additionalText4') || '',
    additionalText5: getText('additionalText5') || '',
    additionalText6: getText('additionalText6') || '',
    additionalText7: getText('additionalText7') || '',
    additionalText8: getText('additionalText8') || '',
    abbreviateMonth: hasAbbreviation
  };
};

const p2_birthMonth = (m: string) => m !== 'MONTH' ? m : 'JANUARY';

// Font-related keys that must NEVER be saved to localStorage.
// Fonts are always retrieved fresh from model_size_text_styles on load.
const FONT_CACHE_KEYS = ['fontFamily', 'isManualFont'] as const;

export const persistTextToCache = (elements: any[], designId: string | undefined) => {
  try {
    const id = designId || 'unsaved';
    const key = `design_tool.personal_form_${id}`;
    const data = extractPersonalFormDataFromElements(elements);

    // Only save if there is actually some content
    const hasContent = Object.values(data).some(v => typeof v === 'string' && v.trim().length > 0);
    if (hasContent) {
      // Strip useDoublePerson — always resets to model default on next load
      // Strip any font keys — fonts come exclusively from model_size_text_styles, never cache
      const { useDoublePerson: _omit, ...rest } = data as any;
      const cacheData = { ...rest };
      FONT_CACHE_KEYS.forEach(k => { delete cacheData[k]; });
      localStorage.setItem(key, JSON.stringify(cacheData));
    }
  } catch (err) {
  }
};


// --- Custom Date Picker Components ---

const DatePickerContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DateInputsRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

const InputSection = styled.div<{ $flex?: string }>`
  position: relative;
  flex: ${props => props.$flex || '1'};
  min-width: 0;
`;

const StyledFieldInput = styled.input`
  width: 100%;
  height: 34px;
  padding: 0 18px 0 6px;
  border: 1.5px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  text-align: center;
  transition: all 0.2s ease;

  &:focus {
    border-color: #5D722C;
    box-shadow: 0 0 0 2px rgba(93, 114, 44, 0.15);
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 500;
  }

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

const DropdownArrowButton = styled.div<{ $isOpen?: boolean; $disabled?: boolean }>`
  position: absolute;
  right: 3px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  color: ${props => props.$disabled ? '#cbd5e1' : '#64748b'};
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$disabled ? 'transparent' : '#f1f5f9'};
    color: ${props => props.$disabled ? '#cbd5e1' : '#5D722C'};
  }

  svg {
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
  }
`;

const GridPopover = styled.div<{ $left?: string; $right?: string; $width?: string }>`
  position: absolute;
  top: calc(100% + 6px);
  ${props => props.$left ? `left: ${props.$left};` : ''}
  ${props => props.$right ? `right: ${props.$right};` : ''}
  ${props => !props.$left && !props.$right ? 'left: 0;' : ''}
  width: ${props => props.$width || '230px'};
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: 10px;
  z-index: 10000;
  user-select: none;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const GridHeaderTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  text-align: center;
`;

const CalendarGridCell = styled.div<{ $isSelected?: boolean; $isRound?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: ${props => props.$isRound ? '1' : 'auto'};
  padding: ${props => props.$isRound ? '0' : '8px 4px'};
  border-radius: ${props => props.$isRound ? '50%' : '8px'};
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.35 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  background: ${props => props.$disabled ? '#f8fafc' : (props.$isSelected ? '#5D722C' : 'transparent')};
  color: ${props => props.$disabled ? '#cbd5e1' : (props.$isSelected ? '#ffffff' : '#1e293b')};
  border: ${props => props.$isRound ? 'none' : `1px solid ${props.$disabled ? '#e2e8f0' : (props.$isSelected ? '#5D722C' : '#f1f5f9')}`};
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.$disabled ? '#f8fafc' : (props.$isSelected ? '#4a5b23' : '#f1f5f9')};
    color: ${props => props.$disabled ? '#cbd5e1' : (props.$isSelected ? '#ffffff' : '#5D722C')};
    border-color: ${props => props.$isRound || props.$disabled ? 'transparent' : '#5D722C'};
  }
`;

const MonthGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`;

const DayGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const YearGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const CalendarPopover = styled.div<{ $top: number, $left: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  z-index: 9999;
  margin-top: 8px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.15);
  border: 1px solid #e2e8f0;
  padding: 16px;
  width: 280px;
  user-select: none;
  max-height: calc(100vh - 20px);
  overflow-y: auto;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ViewSelectorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

const FormScrollContainer = styled.div`
  max-height: 80vh;
  overflow-y: auto;
  scrollbar-width: none; /* Hide scrollbars for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbars for Chrome/Safari/Edge */
  }
  padding: 16px 20px 20px 20px;
  box-sizing: border-box;
`;

const SelectorItem = styled.div<{ $isActive?: boolean }>`
  padding: 10px 8px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  background: ${props => props.$isActive ? '#5D722C' : 'transparent'};
  color: ${props => props.$isActive ? '#fff' : '#1e293b'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$isActive ? '#4a5b23' : '#f1f5f9'};
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const MonthYearDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
  cursor: pointer;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
  }
`;

const NavButton = styled.button`
  background: #f1f5f9;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayHeader = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  padding-bottom: 8px;
`;

const DayCell = styled.div<{ $isSelected?: boolean, $isToday?: boolean, $isOtherMonth?: boolean }>`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  color: ${props => props.$isOtherMonth ? '#cbd5e1' : props.$isSelected ? '#fff' : '#1e293b'};
  background: ${props => props.$isSelected ? '#5D722C' : 'transparent'}; // Monu Green

  &:hover {
    background: ${props => props.$isSelected ? '#4a5b23' : '#f1f5f9'};
  }
`;

export const MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];

export const parseMonthInput = (input: string): string | null => {
  if (!input) return null;
  const clean = input.trim().toLowerCase();
  if (!clean) return null;

  // 1. Numeric check: 1 to 12
  const num = parseInt(clean, 10);
  if (!isNaN(num) && num >= 1 && num <= 12) {
    return MONTHS[num - 1];
  }

  // 2. Exact & Shorthand mappings
  if (clean === 'j' || clean === 'ja' || clean === 'jan' || clean === 'january') return 'JANUARY';
  if (clean === 'f' || clean === 'fe' || clean === 'feb' || clean === 'february') return 'FEBRUARY';
  if (clean === 'm' || clean === 'mar' || clean === 'march' || clean === 'mr') return 'MARCH';
  if (clean === 'a' || clean === 'ap' || clean === 'apr' || clean === 'april') return 'APRIL';
  if (clean === 'may' || clean === 'my') return 'MAY';
  if (clean === 'jun' || clean === 'june' || clean === 'jn') return 'JUNE';
  if (clean === 'jul' || clean === 'july' || clean === 'jl') return 'JULY';
  if (clean === 'au' || clean === 'aug' || clean === 'august' || clean === 'ag') return 'AUGUST';
  if (clean === 's' || clean === 'se' || clean === 'sep' || clean === 'september' || clean === 'sept') return 'SEPTEMBER';
  if (clean === 'o' || clean === 'oc' || clean === 'oct' || clean === 'october') return 'OCTOBER';
  if (clean === 'n' || clean === 'no' || clean === 'nov' || clean === 'november') return 'NOVEMBER';
  if (clean === 'd' || clean === 'de' || clean === 'dec' || clean === 'december') return 'DECEMBER';

  // 3. Fallback: prefix match in MONTHS array
  const found = MONTHS.find(m => m.toLowerCase().startsWith(clean));
  if (found) return found;

  return null;
};

export const getMaxDaysInMonth = (monthName: string, yearStr: string): number => {
  const mIdx = MONTHS.indexOf((monthName || '').toUpperCase());
  if (mIdx === -1) return 31;
  const yVal = parseInt(yearStr, 10);
  const year = !isNaN(yVal) && yVal > 0 ? yVal : 2024;
  return new Date(year, mIdx + 1, 0).getDate();
};

export const formatMonthDisplay = (monthName: string, abbreviateMonth: boolean): string => {
  if (!monthName || monthName === 'MONTH') return '';
  const m = monthName.toUpperCase();
  if (!abbreviateMonth) return m;
  if (m === 'MAY' || m === 'JUNE' || m === 'JULY') return m;
  if (m === 'SEPTEMBER') return 'SEPT.';
  return m.slice(0, 3) + '.';
};

// Custom Green Calendar Icon SVG matching theme
export const GreenCalendarIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="4.5" width="19" height="16" rx="2.5" fill="rgb(250, 251, 252)" stroke="#5D722C" strokeWidth="1.2"/>
    <path d="M2.5 7.5C2.5 5.84315 3.84315 4.5 5.5 4.5H18.5C20.1569 4.5 21.5 5.84315 21.5 7.5V9H2.5V7.5Z" fill="#5D722C"/>
    <rect x="6.5" y="2" width="2" height="4" rx="1" fill="#4a5b23"/>
    <rect x="15.5" y="2" width="2" height="4" rx="1" fill="#4a5b23"/>
    <circle cx="7" cy="12.5" r="1.1" fill="#64748b"/>
    <circle cx="12" cy="12.5" r="1.1" fill="#64748b"/>
    <circle cx="17" cy="12.5" r="1.1" fill="#64748b"/>
    <circle cx="7" cy="16.5" r="1.1" fill="#64748b"/>
    <circle cx="12" cy="16.5" r="1.1" fill="#64748b"/>
    <circle cx="17" cy="16.5" r="1.1" fill="#64748b"/>
  </svg>
);

const DatePickerWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MainInputRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

const SingleDateInput = styled.input`
  flex: 1;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 2px solid rgb(225, 229, 233);
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  background: rgb(250, 251, 252);
  outline: none;
  box-sizing: border-box;
  text-align: center;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;

  &:focus {
    border-color: #5D722C;
    box-shadow: 0 0 0 2px rgba(93, 114, 44, 0.15);
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
    letter-spacing: 0.5px;
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const CalendarIconButton = styled.button<{ $isOpen?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 2px solid ${props => props.$isOpen ? '#5D722C' : 'rgb(225, 229, 233)'};
  border-radius: 6px;
  background: ${props => props.$isOpen ? '#eaf0dc' : 'rgb(250, 251, 252)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$isOpen ? '0 0 0 2px rgba(93, 114, 44, 0.2)' : 'none'};

  &:hover {
    background: ${props => props.$disabled ? 'rgb(250, 251, 252)' : '#eaf0dc'};
    border-color: ${props => props.$disabled ? 'rgb(225, 229, 233)' : '#5D722C'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownPillRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const PillContainer = styled.div<{ $flex?: string }>`
  position: relative;
  flex: ${props => props.$flex || '1'};
  min-width: 0;
`;

const SelectPillButton = styled.button<{ $isOpen?: boolean }>`
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 2px solid ${props => props.$isOpen ? '#5D722C' : 'rgb(225, 229, 233)'};
  border-radius: 8px;
  background: rgb(250, 251, 252);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  outline: none;
  box-shadow: ${props => props.$isOpen ? '0 0 0 2px rgba(93, 114, 44, 0.15)' : 'none'};
  transition: all 0.2s ease;

  &:hover {
    background: #f4f7ed;
    border-color: #5D722C;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: uppercase;
  }

  svg {
    flex-shrink: 0;
    margin-left: 4px;
    color: #64748b;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
  }
`;

export const parseTypedDate = (val: string): { month: string; day: string; year: string } | null => {
  if (!val) return null;
  const clean = val.trim();
  if (!clean) return null;

  // 1. Check raw digits e.g. 01032026 (MMDDYYYY)
  if (/^\d{8}$/.test(clean)) {
    const mNum = parseInt(clean.slice(0, 2), 10);
    const dNum = parseInt(clean.slice(2, 4), 10);
    const yNum = parseInt(clean.slice(4, 8), 10);

    if (mNum >= 1 && mNum <= 12 && dNum >= 1 && dNum <= 31 && yNum >= 1800 && yNum <= 2100) {
      return { month: MONTHS[mNum - 1], day: String(dNum), year: String(yNum) };
    }
  }

  // 2. Delimited formats e.g. 01/03/2026 or Jan 3, 2026
  const parts = clean.split(/[\/\s\.\-]+/).filter(Boolean);
  if (parts.length === 3) {
    const [p1, p2, p3] = parts;

    // Month part
    const mNum = parseInt(p1, 10);
    let monthName: string | null = null;
    if (!isNaN(mNum) && mNum >= 1 && mNum <= 12) {
      monthName = MONTHS[mNum - 1];
    } else {
      monthName = parseMonthInput(p1);
    }

    // Day part
    const dNum = parseInt(p2, 10);
    let dayStr: string | null = null;
    if (!isNaN(dNum) && dNum >= 1 && dNum <= 31) {
      dayStr = String(dNum);
    }

    // Year part
    let yearStr: string | null = null;
    let yNum = parseInt(p3, 10);
    if (!isNaN(yNum)) {
      if (yNum < 100) {
        yNum += (yNum > 50 ? 1900 : 2000);
      }
      if (yNum >= 1800 && yNum <= 2100) {
        yearStr = String(yNum);
      }
    }

    if (monthName && dayStr && yearStr) {
      return { month: monthName, day: dayStr, year: yearStr };
    }
  }

  return null;
};

interface DatePickerProps {
  month: string;
  day: string;
  year: string;
  onChange: (month: string, day: string, year: string) => void;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  abbreviateMonth?: boolean;
  yearOnly?: boolean;
}

export const PremiumDatePicker: React.FC<DatePickerProps> = ({ month, day, year, onChange, disabled, minDate, maxDate, abbreviateMonth = true, yearOnly = false }) => {
  const [isDropdownRowOpen, setIsDropdownRowOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'month' | 'day' | 'year' | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [typedText, setTypedText] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  // Helper to validate and clamp date values against minDate and maxDate
  const clampDateValues = useCallback((m: string, d: string, y: string): { month: string; day: string; year: string } => {
    let newM = m;
    let newD = d;
    let newY = y;

    // Check year against minDate
    if (minDate && newY && newY !== 'YYYY') {
      const yNum = parseInt(newY, 10);
      const minYr = minDate.getFullYear();
      if (!isNaN(yNum) && yNum < minYr) {
        newY = String(minYr);
      }
    }

    // Check month against minDate
    if (minDate && newY && newY !== 'YYYY' && parseInt(newY, 10) === minDate.getFullYear()) {
      if (newM && newM !== 'MONTH') {
        const mIdx = MONTHS.indexOf(newM.toUpperCase());
        const minMIdx = minDate.getMonth();
        if (mIdx !== -1 && mIdx < minMIdx) {
          newM = MONTHS[minMIdx];
        }
      }
    }

    // Check day against minDate
    if (minDate && newY && newY !== 'YYYY' && parseInt(newY, 10) === minDate.getFullYear()) {
      const minMIdx = minDate.getMonth();
      if (newM && newM !== 'MONTH' && MONTHS.indexOf(newM.toUpperCase()) === minMIdx) {
        if (newD && newD !== 'DD') {
          const dNum = parseInt(newD, 10);
          const minDNum = minDate.getDate();
          if (!isNaN(dNum) && dNum < minDNum) {
            newD = String(minDNum);
          }
        }
      }
    }

    // Check year against maxDate
    if (maxDate && newY && newY !== 'YYYY') {
      const yNum = parseInt(newY, 10);
      const maxYr = maxDate.getFullYear();
      if (!isNaN(yNum) && yNum > maxYr) {
        newY = String(maxYr);
      }
    }

    return { month: newM, day: newD, year: newY };
  }, [minDate, maxDate]);

  const handleValueChange = (m: string, d: string, y: string) => {
    const clamped = clampDateValues(m, d, y);
    onChange(clamped.month, clamped.day, clamped.year);
  };

  // Auto-clamp when minDate or maxDate props change
  useEffect(() => {
    if (minDate || maxDate) {
      const clamped = clampDateValues(month, day, year);
      if (clamped.month !== month || clamped.day !== day || clamped.year !== year) {
        onChange(clamped.month, clamped.day, clamped.year);
      }
    }
  }, [minDate, maxDate, month, day, year, clampDateValues, onChange]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format month, day, year for single input display
  const getFormattedDisplayDate = () => {
    if (yearOnly) {
      return year && year !== 'YYYY' ? year : '';
    }

    const hasMonth = month && month !== 'MONTH';
    const hasDay = day && day !== 'DD';
    const hasYear = year && year !== 'YYYY';

    if (!hasMonth && !hasDay && !hasYear) return '';

    const mIdx = hasMonth ? MONTHS.indexOf(month.toUpperCase()) : -1;
    const mm = mIdx !== -1 ? String(mIdx + 1).padStart(2, '0') : 'MM';
    const dd = hasDay ? String(parseInt(day, 10)).padStart(2, '0') : 'DD';
    const yyyy = hasYear ? year : 'YYYY';

    return `${mm} / ${dd} / ${yyyy}`;
  };

  const displayVal = isFocused ? typedText : getFormattedDisplayDate();

  const handleInputFocus = () => {
    setIsFocused(true);
    setTypedText(getFormattedDisplayDate());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTypedText(val);
    if (yearOnly) {
      const cleanDigits = val.replace(/\D/g, '').slice(0, 4);
      if (cleanDigits.length === 4) {
        const num = parseInt(cleanDigits, 10);
        if (num >= 1800 && num <= 2100) {
          handleValueChange('MONTH', 'DD', String(num));
        }
      } else if (cleanDigits.length === 0) {
        handleValueChange('MONTH', 'DD', 'YYYY');
      }
    } else {
      const parsed = parseTypedDate(val);
      if (parsed) {
        handleValueChange(parsed.month, parsed.day, parsed.year);
      }
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (yearOnly) {
      const cleanDigits = typedText.replace(/\D/g, '');
      if (cleanDigits.length === 4) {
        const num = parseInt(cleanDigits, 10);
        if (num >= 1800 && num <= 2100) {
          handleValueChange('MONTH', 'DD', String(num));
          return;
        }
      }
      if (!typedText.trim()) {
        handleValueChange('MONTH', 'DD', 'YYYY');
      }
    } else {
      if (!typedText.trim()) {
        handleValueChange('MONTH', 'DD', 'YYYY');
      } else {
        const parsed = parseTypedDate(typedText);
        if (parsed) {
          handleValueChange(parsed.month, parsed.day, parsed.year);
        }
      }
    }
  };

  const toggleCalendarRow = () => {
    if (disabled) return;
    setIsDropdownRowOpen(prev => !prev);
    setActiveDropdown(null);
  };

  const effectiveMaxDate = (minDate && maxDate && minDate > maxDate) ? minDate : maxDate;
  const maxDays = getMaxDaysInMonth(month, year);
  const maxYearNum = effectiveMaxDate ? effectiveMaxDate.getFullYear() : new Date().getFullYear();
  const minYearNum = minDate ? minDate.getFullYear() : (maxYearNum - 120);
  const yearsList = Array.from({ length: Math.max(1, maxYearNum - minYearNum + 1) }, (_, i) => maxYearNum - i);

  const isYearDisabled = (yNum: number) => {
    if (minDate && yNum < minDate.getFullYear()) return true;
    if (effectiveMaxDate && yNum > effectiveMaxDate.getFullYear()) return true;
    return false;
  };

  const isMonthDisabled = (mName: string) => {
    if (!minDate && !effectiveMaxDate) return false;
    const selectedYr = parseInt(year, 10);
    if (isNaN(selectedYr)) return false;
    const mIdx = MONTHS.indexOf(mName.toUpperCase());
    if (mIdx === -1) return false;

    if (minDate && selectedYr < minDate.getFullYear()) return true;
    if (minDate && selectedYr === minDate.getFullYear() && mIdx < minDate.getMonth()) return true;

    if (effectiveMaxDate && selectedYr > effectiveMaxDate.getFullYear()) return true;
    if (effectiveMaxDate && selectedYr === effectiveMaxDate.getFullYear() && mIdx > effectiveMaxDate.getMonth()) return true;

    return false;
  };

  const isDayDisabled = (dNum: number) => {
    if (!minDate && !effectiveMaxDate) return false;
    const selectedYr = parseInt(year, 10);
    const mIdx = MONTHS.indexOf((month || '').toUpperCase());
    if (isNaN(selectedYr) || mIdx === -1) return false;

    if (minDate) {
      const minYr = minDate.getFullYear();
      const minM = minDate.getMonth();
      const minD = minDate.getDate();
      if (selectedYr < minYr) return true;
      if (selectedYr === minYr && mIdx < minM) return true;
      if (selectedYr === minYr && mIdx === minM && dNum < minD) return true;
    }

    if (effectiveMaxDate) {
      const maxYr = effectiveMaxDate.getFullYear();
      const maxM = effectiveMaxDate.getMonth();
      const maxD = effectiveMaxDate.getDate();
      if (selectedYr > maxYr) return true;
      if (selectedYr === maxYr && mIdx > maxM) return true;
      if (selectedYr === maxYr && mIdx === maxM && dNum > maxD) return true;
    }

    return false;
  };

  const monthPillLabel = (() => {
    if (!month || month === 'MONTH') return 'MONTH';
    return formatMonthDisplay(month, abbreviateMonth);
  })();

  const dayPillLabel = day && day !== 'DD' ? day : 'DD';
  const yearPillLabel = year && year !== 'YYYY' ? year : 'YYYY';

  return (
    <DatePickerWrapper ref={containerRef}>
      {/* Primary Row: Single Text Input + Calendar Icon */}
      <MainInputRow $disabled={disabled}>
        <SingleDateInput
          type="text"
          disabled={disabled}
          placeholder={yearOnly ? "YYYY" : "MM / DD / YYYY"}
          value={displayVal}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        <CalendarIconButton
          type="button"
          $isOpen={isDropdownRowOpen}
          $disabled={disabled}
          onClick={toggleCalendarRow}
          title="Toggle Date Selection Dropdowns"
        >
          <GreenCalendarIcon size={18} />
        </CalendarIconButton>
      </MainInputRow>

      {/* Expanded Row: Dropdown Pills */}
      {isDropdownRowOpen && !disabled && (
        <DropdownPillRow $disabled={disabled}>
          {yearOnly ? (
            /* Year Pill Only when yearOnly is active */
            <PillContainer $flex="1">
              <SelectPillButton
                type="button"
                $isOpen={activeDropdown === 'year'}
                onClick={() => setActiveDropdown(prev => prev === 'year' ? null : 'year')}
              >
                <span>{yearPillLabel}</span>
                <ChevronDown size={12} />
              </SelectPillButton>
              {activeDropdown === 'year' && (
                <GridPopover $left="0" $width="100%">
                  <GridHeaderTitle>Select Year</GridHeaderTitle>
                  <YearGridContainer>
                    {yearsList.map((yNum) => {
                      const isSel = parseInt(year, 10) === yNum;
                      const dis = isYearDisabled(yNum);
                      return (
                        <CalendarGridCell
                          key={yNum}
                          $isSelected={isSel}
                          $disabled={dis}
                          onMouseDown={(e) => {
                            if (dis) return;
                            e.preventDefault();
                            handleValueChange('MONTH', 'DD', String(yNum));
                            setActiveDropdown(null);
                          }}
                        >
                          {yNum}
                        </CalendarGridCell>
                      );
                    })}
                  </YearGridContainer>
                </GridPopover>
              )}
            </PillContainer>
          ) : (
            /* Month, Day, Year Pills */
            <>
              {/* Month Pill */}
              <PillContainer $flex="1.3">
                <SelectPillButton
                  type="button"
                  $isOpen={activeDropdown === 'month'}
                  onClick={() => setActiveDropdown(prev => prev === 'month' ? null : 'month')}
                >
                  <span>{monthPillLabel}</span>
                  <ChevronDown size={12} />
                </SelectPillButton>
                {activeDropdown === 'month' && (
                  <GridPopover $left="0" $width="210px">
                    <GridHeaderTitle>Select Month</GridHeaderTitle>
                    <MonthGridContainer>
                      {MONTHS.map((mName) => {
                        const isSel = (month || '').toUpperCase() === mName;
                        const label = formatMonthDisplay(mName, true).replace('.', '');
                        const dis = isMonthDisabled(mName);
                        return (
                          <CalendarGridCell
                            key={mName}
                            $isSelected={isSel}
                            $disabled={dis}
                            onMouseDown={(e) => {
                              if (dis) return;
                              e.preventDefault();
                              const newMax = getMaxDaysInMonth(mName, year);
                              const currentDayNum = parseInt(day, 10);
                              let nextDay = day;
                              if (!isNaN(currentDayNum) && currentDayNum > newMax) {
                                nextDay = String(newMax);
                              }
                              handleValueChange(mName, nextDay, year);
                              setActiveDropdown(null);
                            }}
                          >
                            {label}
                          </CalendarGridCell>
                        );
                      })}
                    </MonthGridContainer>
                  </GridPopover>
                )}
              </PillContainer>

              {/* Day Pill */}
              <PillContainer $flex="0.8">
                <SelectPillButton
                  type="button"
                  $isOpen={activeDropdown === 'day'}
                  onClick={() => setActiveDropdown(prev => prev === 'day' ? null : 'day')}
                >
                  <span>{dayPillLabel}</span>
                  <ChevronDown size={12} />
                </SelectPillButton>
                {activeDropdown === 'day' && (
                  <GridPopover $left="-50px" $width="220px">
                    <GridHeaderTitle>Select Day</GridHeaderTitle>
                    <DayGridContainer>
                      {Array.from({ length: maxDays }, (_, i) => i + 1).map((dNum) => {
                        const isSel = parseInt(day, 10) === dNum;
                        const dis = isDayDisabled(dNum);
                        return (
                          <CalendarGridCell
                            key={dNum}
                            $isRound={true}
                            $isSelected={isSel}
                            $disabled={dis}
                            onMouseDown={(e) => {
                              if (dis) return;
                              e.preventDefault();
                              handleValueChange(month, String(dNum), year);
                              setActiveDropdown(null);
                            }}
                          >
                            {dNum}
                          </CalendarGridCell>
                        );
                      })}
                    </DayGridContainer>
                  </GridPopover>
                )}
              </PillContainer>

              {/* Year Pill */}
              <PillContainer $flex="1.1">
                <SelectPillButton
                  type="button"
                  $isOpen={activeDropdown === 'year'}
                  onClick={() => setActiveDropdown(prev => prev === 'year' ? null : 'year')}
                >
                  <span>{yearPillLabel}</span>
                  <ChevronDown size={12} />
                </SelectPillButton>
                {activeDropdown === 'year' && (
                  <GridPopover $right="0" $width="220px">
                    <GridHeaderTitle>Select Year</GridHeaderTitle>
                    <YearGridContainer>
                      {yearsList.map((yNum) => {
                        const isSel = parseInt(year, 10) === yNum;
                        const dis = isYearDisabled(yNum);
                        return (
                          <CalendarGridCell
                            key={yNum}
                            $isSelected={isSel}
                            $disabled={dis}
                            onMouseDown={(e) => {
                              if (dis) return;
                              e.preventDefault();
                              handleValueChange(month, day, String(yNum));
                              setActiveDropdown(null);
                            }}
                          >
                            {yNum}
                          </CalendarGridCell>
                        );
                      })}
                    </YearGridContainer>
                  </GridPopover>
                )}
              </PillContainer>
            </>
          )}
        </DropdownPillRow>
      )}
    </DatePickerWrapper>
  );
};

interface TextProps {
  onClose: () => void;
  onConfirm: (formData: any) => void;
  isTemplateSelected: boolean;
  selectedColorIndex: number;
  colors: any[];
  preloadedVerses?: any[];
  preloadedCategories?: string[];
}

// Separate component for Personal Details Form to prevent re-renders
interface PersonalDetailsFormProps {
  initialData?: any;
  onChange?: (formData: any) => void;
  selectedColorIndex: number;
  colors: any[];
  preloadedVerses?: any[];
  preloadedCategories?: string[];
  setPersonalFormData?: (data: any) => void;
  side?: 'front' | 'back';
}

const PersonalDetailsForm = React.memo((props: PersonalDetailsFormProps) => {
  const {
    initialData,
    onChange,
    selectedColorIndex,
    colors,
    preloadedVerses,
    preloadedCategories,
    side = 'front'
  } = props;
  const { currentDesign, updateDesign, sharedFonts, sharedColors, authedUser } = useDesignContext();
  const [showExtractionModal, setShowExtractionModal] = useState(false);

  const userRole = String(authedUser?.role || authedUser?.user_type_flag || authedUser?.user_type || '').toLowerCase().trim();
  const isAdminOrOrderCare = authedUser?.is_editor === 'yes' || ['admin', 'order_care', 'customer_care', 'ordercare', 'customercare', 'staff', 'editor', 'super_admin', 'manager'].includes(userRole);
  const frontLastName = useMemo(() => {
    if (!currentDesign?.elements) return '';
    const el = currentDesign.elements.find((e: any) => e.id === 'lastName' && (e.side === 'front' || !e.side));
    return el?.properties?.content || '';
  }, [currentDesign?.elements]);
  const defaultPlaceholders = {
    firstName: '',
    middleName: '',
    lastName: '',
    birthMonth: 'JANUARY',
    birthDay: '1',
    birthYear: '1940',
    deathMonth: 'MONTH',
    deathDay: 'DD',
    deathYear: 'YYYY',
    hideDeathDate: false,
    selectedVerse: 'Select a verse or enter custom text',
    customVerse: '',
    hideVerse: false,
    hideLastName: false,
    verse: '',
    additionalText: '',
    additionalText1: '',
    additionalText2: '',
    additionalText3: '',
    additionalText4: '',
    additionalText5: '',
    additionalText6: '',
    additionalText7: '',
    additionalText8: '',
    p2_firstName: '',
    p2_middleName: '',
    p2_birthMonth: 'JANUARY',
    p2_birthDay: '1',
    p2_birthYear: '1940',
    p2_deathMonth: 'MONTH',
    p2_deathDay: 'DD',
    p2_deathYear: 'YYYY',
    p2_hideDeathDate: false,
    useDoublePerson: false,
    weddingMonth: 'MONTH',
    weddingDay: 'DD',
    weddingYear: 'YYYY',
    hideWeddingDate: true,
    abbreviateMonth: true
  };

  const selectedSizeName = (currentDesign as any)?.selectedSizeName || '';
  const isDoubleModelSize =
    selectedSizeName.includes('36x6x24') || selectedSizeName.includes('36x24x6') ||
    selectedSizeName.includes('48x6x24') || selectedSizeName.includes('48x24x6') ||
    selectedSizeName.includes('54x8x28') || selectedSizeName.includes('54x28x8') ||
    selectedSizeName.toLowerCase().includes('double');

  // Cache verse API responses to avoid redundant network requests when switching categories
  const verseFetchCacheRef = useRef<Map<string, any[]>>(new Map());

  const [formData, setFormData] = useState(() => {
    const initial = { ...defaultPlaceholders, ...(initialData || {}) };
    // Always enable double person for double model sizes
    if (isDoubleModelSize) {
      initial.useDoublePerson = true;
    }
    return initial;
  });

  // Sync formData with initialData prop changes
  useEffect(() => {
    if (initialData) {
      setFormData((prev: any) => {
        // Prevent feedback loop: only update if data is actually different
        const isSame = JSON.stringify(prev) === JSON.stringify(initialData);
        if (isSame) return prev;
        return { ...prev, ...initialData };
      });
    }
  }, [initialData]);
  const [activeTab, setActiveTab] = useState<'p1' | 'p2'>('p1');
  const [error, setError] = useState<string | null>(null);
  const [verses, setVerses] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    if (initialData?.selectedVerse === 'Custom') return 'Custom';
    if (initialData?.selectedCategory) return initialData.selectedCategory;
    return '';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [visibleAdditionalCount, setVisibleAdditionalCount] = useState(0);
  const [focusNewIndex, setFocusNewIndex] = useState<number | null>(null);

  const additionalTextRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  // Auto-focus new additional text field when added
  useEffect(() => {
    if (focusNewIndex !== null && additionalTextRefs.current[focusNewIndex]) {
      additionalTextRefs.current[focusNewIndex]?.focus();
      setFocusNewIndex(null);
    }
  }, [focusNewIndex, visibleAdditionalCount]);

  // Sync double person state when size changes
  useEffect(() => {
    if (!isDoubleModelSize && formData.useDoublePerson) {
      const next = { ...formData, useDoublePerson: false };
      setFormData(next);
      setActiveTab('p1');
      if (onChange) {
        try { onChange(next); } catch { }
      }
    }
  }, [isDoubleModelSize, formData.useDoublePerson, onChange, formData]);

  // Fetch verses based on selected category (with in-memory caching to avoid duplicate requests)
  const fetchVerses = useCallback(async (category: string) => {
    // Return cached result immediately if available
    if (verseFetchCacheRef.current.has(category)) {
      const cached = verseFetchCacheRef.current.get(category)!;
      setVerses(cached);
      setFormData((prev: any) => {
        if (prev.selectedVerse === 'Custom' && prev.customVerse) {
          const contentToCheck = prev.customVerse.trim().toLowerCase();
          const match = cached.find((v: any) => v.content.trim().toLowerCase() === contentToCheck);
          if (match) {
            const next = { ...prev, selectedVerse: match.content };
            if (onChange) try { onChange(next); } catch { }
            return next;
          }
        }
        return prev;
      });
      return;
    }

    try {
      setIsLoading(true);
      const url = category === 'All'
        ? buildApiUrl('/api/verses')
        : buildApiUrl(`/api/verses?category=${encodeURIComponent(category)}`);

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'success') {
        const fetchedVerses = data.data.verses;
        // Store in cache before updating state
        verseFetchCacheRef.current.set(category, fetchedVerses);
        setVerses(fetchedVerses);

        // If current selection is 'Custom' but the content matches a fetched verse,
        // switch selection to that verse automatically.
        setFormData((prev: any) => {
          if (prev.selectedVerse === 'Custom' && prev.customVerse) {
            const contentToCheck = prev.customVerse.trim().toLowerCase();
            const match = fetchedVerses.find((v: any) => v.content.trim().toLowerCase() === contentToCheck);
            if (match) {
              const next = { ...prev, selectedVerse: match.content };
              if (onChange) try { onChange(next); } catch { }
              return next;
            }
          }
          return prev;
        });
      }
    } catch (error) {
      setError('Failed to load verses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch verses and categories when component mounts if not preloaded
  useEffect(() => {
    if (preloadedVerses && preloadedCategories) {
      setVerses(preloadedVerses);
      const filtered = (preloadedCategories || []).filter((c: string) => c !== 'All');
      setCategories(filtered);

      if (selectedCategory === '' || selectedCategory === 'All') {
        if (formData.selectedVerse && formData.selectedVerse !== 'Custom') {
          const match = (preloadedVerses || []).find((v: any) => v.content.trim().toLowerCase() === formData.selectedVerse.trim().toLowerCase());
          if (match && match.category) {
            setSelectedCategory(match.category);
            const filtered = (preloadedCategories || []).filter((c: string) => c !== 'All');
            setCategories(filtered);
            return;
          }
        }
        if (filtered.length > 0) setSelectedCategory(filtered[0]);
        else setSelectedCategory('Custom');
      }
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Fetch categories first
        const categoriesResponse = await fetch(buildApiUrl('/api/verses/categories'));
        const categoriesData = await categoriesResponse.json();
        if (categoriesData.status === 'success') {
          const fetched = (Array.isArray(categoriesData.data) ? categoriesData.data : []) as string[];
          const unique = Array.from(new Set(fetched.filter((c: string) => c !== 'All')));
          setCategories([...unique, 'Custom']);

          if (unique.length > 0) {
            const first = unique[0];
            setSelectedCategory(first);
            await fetchVerses(first);
          } else {
            setSelectedCategory('Custom');
            await fetchVerses('Custom');
          }
        }
      } catch (error) {
        setError('Failed to load verses. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [preloadedVerses, preloadedCategories, fetchVerses]);



  const handleCategoryChange = async (category: string) => {
    setSelectedCategory(category);
    if (category === 'Custom') {
      setVerses([]);
      const next = { ...formData, selectedCategory: 'Custom', selectedVerse: 'Custom' };
      setFormData(next);
      if (onChange) try { onChange(next); } catch { }
      return;
    }
    // Reset selected verse when switching away from Custom to prevent UI ghosting
    const next = { ...formData, selectedCategory: category, selectedVerse: '' };
    setFormData(next);
    if (onChange) try { onChange(next); } catch { }
    await fetchVerses(category);
  };

  useEffect(() => {
    if (initialData) {
      // Normalize initialData so placeholder-like strings are not treated as real values
      const normalized = { ...initialData } as any;
      try {
        ['firstName', 'middleName', 'lastName', 'additionalText', 'additionalText1', 'additionalText2', 'additionalText3', 'additionalText4', 'additionalText5', 'additionalText6', 'additionalText7', 'additionalText8', 'p2_firstName', 'p2_middleName'].forEach((f) => {
          if (typeof normalized[f] === 'string') {
            const rawV = normalized[f] || '';
            const v = rawV.trim();
            const placeholders = ['First Name', 'Middle Name', 'Last Name', 'Additional Text'];
            if (!v || placeholders.includes(v)) {
              normalized[f] = '';
            } else {
              const limit = f.startsWith('additionalText') ? 500 : 100;
              normalized[f] = rawV.slice(0, limit);
            }
          }
        });
      } catch (e) {
        // ignore normalization errors
      }

      // Legacy migration
      if (normalized.additionalText && !normalized.additionalText1) {
        normalized.additionalText1 = normalized.additionalText;
      }

      // Pack additional text fields to remove gaps (caused by canvas deletions)
      const packedAddTexts: string[] = [];
      for (let i = 1; i <= 8; i++) {
        const val = normalized[`additionalText${i}`];
        if (typeof val === 'string' && val !== '') {
          packedAddTexts.push(val);
        }
      }

      // Clear out then fill with packed values
      for (let i = 1; i <= 8; i++) {
        normalized[`additionalText${i}`] = packedAddTexts[i - 1] || '';
      }

      setFormData((prev: any) => {
        // Prevent feedback loop: only update if data is actually different
        const isSame = JSON.stringify(prev) === JSON.stringify(normalized);
        if (isSame) return prev;

        // Sync category state if the loaded verse is 'Custom'
        if (normalized.selectedVerse === 'Custom') {
          setSelectedCategory('Custom');
        } else if (normalized.selectedCategory) {
          setSelectedCategory(normalized.selectedCategory);
        }

        return { ...prev, ...normalized };
      });

      // Update visible count based on packed data
      setVisibleAdditionalCount(packedAddTexts.length);
    } else {
      // If initialData is null, reset to defaults
      setFormData({ ...defaultPlaceholders, useDoublePerson: isDoubleModelSize && side === 'front' });
      setVisibleAdditionalCount(0);
    }
  }, [initialData, isDoubleModelSize, side]);

  const handleInputChange = useCallback((fieldOrUpdates: string | Record<string, string | boolean>, value?: string | boolean) => {
    const rawUpdates: Record<string, any> = typeof fieldOrUpdates === 'string'
      ? { [fieldOrUpdates]: value }
      : fieldOrUpdates;

    setFormData((prev: any) => {
      const next = { ...prev };
      let changed = false;

      for (const [field, val] of Object.entries(rawUpdates)) {
        const isAdditionalText = field.toLowerCase().startsWith('additionaltext');

        let nextVal: any = val;
        if (typeof val === 'string') {
          const isVerseField = field === 'verse' || field === 'selectedVerse' || field === 'customVerse';
          const limit = isAdditionalText ? 500 : (isVerseField ? 200 : 100);
          const isWritingField = isVerseField || isAdditionalText;
          if (!isWritingField) {
            if (field === 'lastName' || field === 'fullName') {
              nextVal = adjustLastNameCasing(val).slice(0, limit);
            } else {
              nextVal = val.toUpperCase().slice(0, limit);
            }
          } else {
            nextVal = val.slice(0, limit);
          }
        }

        if (prev[field] !== nextVal) {
          next[field] = nextVal;
          changed = true;

          // Automatically uncheck "I don't want verse" and ensure selectedVerse='Custom' & selectedCategory='Custom' if user types a custom verse
          if (field === 'customVerse' && typeof nextVal === 'string' && nextVal.trim() !== '') {
            next['hideVerse'] = false;
            next['selectedVerse'] = 'Custom';
            next['selectedCategory'] = 'Custom';
          } else if (field === 'selectedVerse' && typeof nextVal === 'string' && nextVal.trim() !== '') {
            next['hideVerse'] = false;
            if (nextVal !== 'Custom' && selectedCategory && selectedCategory !== 'Custom') {
              next['selectedCategory'] = selectedCategory;
            }
          }
        }
      }

      if (!changed) return prev;

      // Pulse update to parent outside of this render cycle to avoid "struck" behavior
      if (onChange) {
        setTimeout(() => {
          try { onChange(next); } catch (e) { console.error("Error in onChange:", e); }
        }, 0);
      }

      return next;
    });
  }, [onChange]);

  const handleRemoveAdditionalText = (indexToRemove: number) => {
    setFormData((prev: any) => {
      const next = { ...prev };
      // Shift up values
      for (let i = indexToRemove; i < 8; i++) {
        const nextVal = next[`additionalText${i + 1}`];
        next[`additionalText${i}`] = nextVal;
      }
      // Clear last one which is now empty or moved up
      next[`additionalText8`] = '';

      try { onChange && onChange(next); } catch { }
      return next;
    });
    setVisibleAdditionalCount(prev => Math.max(0, prev - 1));
  };



  const parseExtractedDateString = (dateStr: string) => {
    if (!dateStr || typeof dateStr !== 'string') return { month: 'MONTH', day: 'DD', year: 'YYYY', isEmpty: true };
    const str = dateStr.trim();
    if (!str) return { month: 'MONTH', day: 'DD', year: 'YYYY', isEmpty: true };

    const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (isoMatch) {
      const y = isoMatch[1];
      const mIdx = parseInt(isoMatch[2], 10) - 1;
      const d = String(parseInt(isoMatch[3], 10));
      const m = (mIdx >= 0 && mIdx < 12) ? MONTH_NAMES[mIdx] : 'MONTH';
      return { month: m, day: d, year: y, isEmpty: false };
    }

    const tokens = str.replace(/[.,\/]/g, ' ').toUpperCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 1 && /^\d{4}$/.test(tokens[0])) {
      return { month: 'MONTH', day: 'DD', year: tokens[0], isEmpty: false };
    }

    if (tokens.length >= 3) {
      const foundM = MONTH_NAMES.find(m => m.startsWith(tokens[0]) || tokens[0].startsWith(m.slice(0, 3)));
      const dayVal = tokens[1].replace(/\D/g, '');
      const yearVal = tokens[2].replace(/\D/g, '');
      return {
        month: foundM || 'MONTH',
        day: dayVal || '1',
        year: yearVal || '1940',
        isEmpty: false
      };
    }

    return { month: 'MONTH', day: 'DD', year: 'YYYY', isEmpty: false };
  };

  const handleApplyExtractedData = useCallback((extractedPersons: ExtractedPerson[], meta: ExtractionMeta) => {
    if (!extractedPersons || extractedPersons.length === 0) return;

    const updates: Record<string, any> = {};
    const p1 = extractedPersons[0];

    // Person 1 Names
    updates.firstName = p1.first_name || '';
    updates.middleName = p1.middle_name || '';
    updates.lastName = p1.last_name || p1.family_name || '';

    // Person 1 Birth Date
    if (p1.birth_date) {
      const dob = parseExtractedDateString(p1.birth_date);
      if (!dob.isEmpty) {
        updates.birthMonth = dob.month !== 'MONTH' ? dob.month : 'JANUARY';
        updates.birthDay = dob.day !== 'DD' ? dob.day : '1';
        updates.birthYear = dob.year !== 'YYYY' ? dob.year : '1940';
      } else {
        updates.birthMonth = 'JANUARY';
        updates.birthDay = '1';
        updates.birthYear = '1940';
      }
    } else {
      updates.birthMonth = 'JANUARY';
      updates.birthDay = '1';
      updates.birthYear = '1940';
    }

    // Person 1 Death Date
    if (p1.death_date) {
      const dod = parseExtractedDateString(p1.death_date);
      if (!dod.isEmpty) {
        updates.deathMonth = dod.month;
        updates.deathDay = dod.day;
        updates.deathYear = dod.year;
        updates.hideDeathDate = false;
      } else {
        updates.deathMonth = 'MONTH';
        updates.deathDay = 'DD';
        updates.deathYear = 'YYYY';
        updates.hideDeathDate = true;
      }
    } else {
      updates.deathMonth = 'MONTH';
      updates.deathDay = 'DD';
      updates.deathYear = 'YYYY';
      updates.hideDeathDate = true;
    }

    // Person 1 Wedding Date
    if (p1.wedding_date) {
      const wed = parseExtractedDateString(p1.wedding_date);
      if (!wed.isEmpty) {
        updates.weddingMonth = wed.month;
        updates.weddingDay = wed.day;
        updates.weddingYear = wed.year;
        updates.hideWeddingDate = false;
      } else {
        updates.weddingMonth = 'MONTH';
        updates.weddingDay = 'DD';
        updates.weddingYear = 'YYYY';
        updates.hideWeddingDate = true;
      }
    } else {
      updates.weddingMonth = 'MONTH';
      updates.weddingDay = 'DD';
      updates.weddingYear = 'YYYY';
      updates.hideWeddingDate = true;
    }

    // Person 2 Details
    if (extractedPersons.length >= 2) {
      updates.useDoublePerson = true;
      const p2 = extractedPersons[1];
      updates.p2_firstName = p2.first_name || '';
      updates.p2_middleName = p2.middle_name || '';

      if (p2.birth_date) {
        const dob2 = parseExtractedDateString(p2.birth_date);
        if (!dob2.isEmpty) {
          updates.p2_birthMonth = dob2.month !== 'MONTH' ? dob2.month : 'JANUARY';
          updates.p2_birthDay = dob2.day !== 'DD' ? dob2.day : '1';
          updates.p2_birthYear = dob2.year !== 'YYYY' ? dob2.year : '1940';
        } else {
          updates.p2_birthMonth = 'JANUARY';
          updates.p2_birthDay = '1';
          updates.p2_birthYear = '1940';
        }
      } else {
        updates.p2_birthMonth = 'JANUARY';
        updates.p2_birthDay = '1';
        updates.p2_birthYear = '1940';
      }

      if (p2.death_date) {
        const dod2 = parseExtractedDateString(p2.death_date);
        if (!dod2.isEmpty) {
          updates.p2_deathMonth = dod2.month;
          updates.p2_deathDay = dod2.day;
          updates.p2_deathYear = dod2.year;
          updates.p2_hideDeathDate = false;
        } else {
          updates.p2_deathMonth = 'MONTH';
          updates.p2_deathDay = 'DD';
          updates.p2_deathYear = 'YYYY';
          updates.p2_hideDeathDate = true;
        }
      } else {
        updates.p2_deathMonth = 'MONTH';
        updates.p2_deathDay = 'DD';
        updates.p2_deathYear = 'YYYY';
        updates.p2_hideDeathDate = true;
      }
    } else {
      updates.p2_firstName = '';
      updates.p2_middleName = '';
      updates.p2_birthMonth = 'JANUARY';
      updates.p2_birthDay = '1';
      updates.p2_birthYear = '1940';
      updates.p2_deathMonth = 'MONTH';
      updates.p2_deathDay = 'DD';
      updates.p2_deathYear = 'YYYY';
      updates.p2_hideDeathDate = true;
      if (!isDoubleModelSize) {
        updates.useDoublePerson = false;
      }
    }

    // Verse
    const extractedVerse = p1.verse || extractedPersons[1]?.verse || '';
    if (extractedVerse) {
      updates.selectedVerse = 'Custom';
      updates.selectedCategory = 'Custom';
      updates.customVerse = extractedVerse;
      updates.verse = extractedVerse;
      updates.hideVerse = false;
      setSelectedCategory('Custom');
    } else {
      updates.selectedVerse = 'Select a verse or enter custom text';
      updates.selectedCategory = '';
      updates.customVerse = '';
      updates.verse = '';
      updates.hideVerse = true;
    }

    // Additional Texts
    const allAddTexts: string[] = [];
    extractedPersons.forEach(p => {
      (p.additional_texts || []).forEach(t => {
        if (t && t.trim()) allAddTexts.push(t.trim());
      });
    });

    for (let i = 1; i <= 8; i++) {
      updates[`additionalText${i}`] = allAddTexts[i - 1] || '';
    }
    updates.additionalText = allAddTexts[0] || '';
    setVisibleAdditionalCount(allAddTexts.length);

    handleInputChange(updates);
    setShowExtractionModal(false);
  }, [handleInputChange, isDoubleModelSize]);

  return (
    <FormScrollContainer>
      {isAdminOrOrderCare && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          background: '#f8f9fa',
          border: '1px solid #e1e5e9',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ORDERCARE EXTRACTION TOOL
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
              Upload order screenshot or image to fill fields
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowExtractionModal(true)}
            style={{
              padding: '8px 16px',
              background: '#5D722C',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#4a5b23')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#5D722C')}
          >
            📷 Upload / Extract Order
          </button>
        </div>
      )}

      {side === 'front' && isDoubleModelSize && (
        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e1e5e9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#34495e' }}>DOUBLE PERSON DESIGN</span>
            <div
              onClick={() => {
                const nextVal = !formData.useDoublePerson;
                handleInputChange('useDoublePerson', nextVal);
                if (!nextVal) setActiveTab('p1');
              }}
              style={{
                width: '44px',
                height: '24px',
                background: formData.useDoublePerson ? '#5D722C' : '#cbd5e1',
                borderRadius: '12px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                width: '18px',
                height: '18px',
                background: '#fff',
                borderRadius: '50%',
                position: 'absolute',
                top: '3px',
                left: formData.useDoublePerson ? '23px' : '3px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </div>
          </div>
        </div>
      )}

      {formData.useDoublePerson && side === 'front' && (
        <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e1e5e9' }}>
          <button
            onClick={() => setActiveTab('p1')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'p1' ? '#fff' : '#f8f9fa',
              borderBottom: `2px solid ${activeTab === 'p1' ? '#5D722C' : 'transparent'}`,
              color: activeTab === 'p1' ? '#5D722C' : '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '12px',
              textTransform: 'uppercase'
            }}
          >
            Person 1
          </button>
          <button
            onClick={() => setActiveTab('p2')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'p2' ? '#fff' : '#f8f9fa',
              borderBottom: `2px solid ${activeTab === 'p2' ? '#5D722C' : 'transparent'}`,
              color: activeTab === 'p2' ? '#5D722C' : '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '12px',
              textTransform: 'uppercase'
            }}
          >
            Person 2
          </button>
        </div>
      )}

      {/* Name Fields */}
      <div style={{ marginBottom: '20px' }}>
        {side === 'front' && ((activeTab === 'p1' || !formData.useDoublePerson) ? (
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>First Name</label>
              <input
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                maxLength={26}
                placeholder="Enter first name"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '5px',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  outline: 'none',
                  textTransform: 'uppercase'
                }}
                onFocus={(e) => e.target.style.borderColor = '#5D722C'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Middle Name (Optional)</label>
              <input
                type="text"
                value={formData.middleName || ''}
                onChange={(e) => handleInputChange('middleName', e.target.value)}
                maxLength={26}
                placeholder="Enter middle name"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '5px',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  outline: 'none',
                  textTransform: 'uppercase'
                }}
                onFocus={(e) => e.target.style.borderColor = '#5D722C'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Person 2 First Name</label>
              <input
                type="text"
                value={formData.p2_firstName || ''}
                onChange={(e) => handleInputChange('p2_firstName', e.target.value)}
                required
                maxLength={26}
                placeholder="Enter person 2 first name"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '5px',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  outline: 'none',
                  textTransform: 'uppercase'
                }}
                onFocus={(e) => e.target.style.borderColor = '#5D722C'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Person 2 Middle Name (Optional)</label>
              <input
                type="text"
                value={formData.p2_middleName || ''}
                onChange={(e) => handleInputChange('p2_middleName', e.target.value)}
                maxLength={26}
                placeholder="Enter person 2 middle name"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '5px',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  outline: 'none',
                  textTransform: 'uppercase'
                }}
                onFocus={(e) => e.target.style.borderColor = '#5D722C'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            </div>
          </>
        ))}

        {/* Common Last Name (Shared for both people in double designs) */}
        {(side === 'front' || side === 'back') && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Name</label>
            {side === 'front' ? (
              <input
                type="text"
                value={formData.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required={!formData.hideLastName}
                maxLength={26}
                placeholder="Enter last name"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '5px',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  outline: 'none',
                  textTransform: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#5D722C'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <input
                  type="checkbox"
                  id="includeBackLastName"
                  checked={!!formData.lastName && !formData.hideLastName}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (!frontLastName) {
                        setError('Type the Last name in front side');
                        setTimeout(() => setError(null), 3000);
                        return;
                      }
                      setError(null);
                      handleInputChange('lastName', frontLastName);
                      handleInputChange('hideLastName', false);
                    } else {
                      handleInputChange('lastName', '');
                      handleInputChange('hideLastName', true);
                    }
                  }}
                  style={{ marginRight: '10px', width: '16px', height: '16px', accentColor: '#5D722C' }}
                />
                <label htmlFor="includeBackLastName" style={{ fontSize: '12px', color: '#495057', cursor: 'pointer', fontWeight: '500' }}>
                  Include Last Name ({frontLastName || 'Not set'})
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      {side === 'front' && (
        <>
          {/* Date fields with Abbreviate Month checkbox on right */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
            {/* Left side: Date fields */}
            <div style={{ width: 'calc(65% - 6px)', flexShrink: 0 }}>
              {/* Date of Birth */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {formData.useDoublePerson ? `${activeTab === 'p1' ? 'Person 1' : 'Person 2'} Date of Birth` : 'Date of Birth'}
                </label>
                <div style={{ width: '100%' }}>
                  <PremiumDatePicker
                    month={activeTab === 'p1' ? formData.birthMonth : formData.p2_birthMonth}
                    day={activeTab === 'p1' ? formData.birthDay : formData.p2_birthDay}
                    year={activeTab === 'p1' ? formData.birthYear : formData.p2_birthYear}
                    maxDate={new Date()}
                    abbreviateMonth={formData.abbreviateMonth !== false}
                    yearOnly={formData.yearOnly === true}
                    onChange={(m, d, y) => {
                      const prefix = activeTab === 'p1' ? '' : 'p2_';
                      handleInputChange({
                        [`${prefix}birthMonth`]: m,
                        [`${prefix}birthDay`]: d,
                        [`${prefix}birthYear`]: y
                      });
                    }}
                  />
                </div>
              </div>

              {/* Date of Death */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {formData.useDoublePerson ? `${activeTab === 'p1' ? 'Person 1' : 'Person 2'} Date of Death` : 'Date of Death'}
                </label>
                <div style={{ width: '100%' }}>
                  <PremiumDatePicker
                    month={activeTab === 'p1' ? formData.deathMonth : formData.p2_deathMonth}
                    day={activeTab === 'p1' ? formData.deathDay : formData.p2_deathDay}
                    year={activeTab === 'p1' ? formData.deathYear : formData.p2_deathYear}
                    disabled={activeTab === 'p1' ? formData.hideDeathDate : formData.p2_hideDeathDate}
                    abbreviateMonth={formData.abbreviateMonth !== false}
                    yearOnly={formData.yearOnly === true}
                    minDate={(() => {
                      const birthMonth = activeTab === 'p1' ? formData.birthMonth : formData.p2_birthMonth;
                      const birthDay = activeTab === 'p1' ? formData.birthDay : formData.p2_birthDay;
                      const birthYear = activeTab === 'p1' ? formData.birthYear : formData.p2_birthYear;
                      if (birthMonth !== 'MONTH' && birthDay !== 'DD' && birthYear !== 'YYYY') {
                        const mIdx = MONTHS.indexOf(birthMonth.toUpperCase());
                        if (mIdx !== -1) return new Date(parseInt(birthYear), mIdx, parseInt(birthDay));
                      }
                      return undefined;
                    })()}
                    maxDate={new Date()}
                    onChange={(m, d, y) => {
                      const prefix = activeTab === 'p1' ? '' : 'p2_';
                      handleInputChange({
                        [`${prefix}deathMonth`]: m,
                        [`${prefix}deathDay`]: d,
                        [`${prefix}deathYear`]: y
                      });
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <input
                    type="checkbox"
                    id={`hideDeathDate_${activeTab}`}
                    checked={activeTab === 'p1' ? formData.hideDeathDate : formData.p2_hideDeathDate}
                    onChange={(e) => handleInputChange(activeTab === 'p1' ? 'hideDeathDate' : 'p2_hideDeathDate', e.target.checked)}
                    style={{ marginRight: '10px', width: '16px', height: '16px', accentColor: '#5D722C' }}
                  />
                  <label htmlFor={`hideDeathDate_${activeTab}`} style={{ fontSize: '12px', color: '#495057', cursor: 'pointer', fontWeight: '500' }}>
                    I don't want death date
                  </label>
                </div>
              </div>
            </div>

            {/* Right side: Abbreviate Month & Year Only checkboxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px 8px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef', width: 'calc(35% - 6px)', boxSizing: 'border-box', marginTop: '23px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id={`abbreviateMonth_${activeTab}`}
                  checked={formData.abbreviateMonth !== false}
                  onChange={(e) => handleInputChange('abbreviateMonth', e.target.checked)}
                  style={{ marginRight: '8px', width: '16px', height: '16px', accentColor: '#5D722C', flexShrink: 0 }}
                />
                <label htmlFor={`abbreviateMonth_${activeTab}`} style={{ fontSize: '11px', color: '#495057', cursor: 'pointer', fontWeight: '500', lineHeight: '1.4' }}>
                  Abbreviate Month<br />(JAN. vs JANUARY)
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id={`yearOnly_${activeTab}`}
                  checked={formData.yearOnly === true}
                  onChange={(e) => handleInputChange('yearOnly', e.target.checked)}
                  style={{ marginRight: '8px', width: '16px', height: '16px', accentColor: '#5D722C', flexShrink: 0 }}
                />
                <label htmlFor={`yearOnly_${activeTab}`} style={{ fontSize: '11px', color: '#495057', cursor: 'pointer', fontWeight: '500', lineHeight: '1.4' }}>
                  Year only
                </label>
              </div>
            </div>
          </div>

          {/* Wedding Date (Double Person) */}
          {formData.useDoublePerson && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Wedding Date</label>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <PremiumDatePicker
                  month={formData.weddingMonth}
                  day={formData.weddingDay}
                  year={formData.weddingYear}
                  disabled={formData.hideWeddingDate}
                  abbreviateMonth={formData.abbreviateMonth !== false}
                  minDate={(() => {
                    const dates: Date[] = [];
                    if (formData.birthMonth !== 'MONTH' && formData.birthDay !== 'DD' && formData.birthYear !== 'YYYY') {
                      const idx = MONTHS.indexOf(formData.birthMonth.toUpperCase());
                      if (idx !== -1) dates.push(new Date(parseInt(formData.birthYear, 10), idx, parseInt(formData.birthDay, 10)));
                    }
                    if (formData.p2_birthMonth !== 'MONTH' && formData.p2_birthDay !== 'DD' && formData.p2_birthYear !== 'YYYY') {
                      const idx = MONTHS.indexOf(formData.p2_birthMonth.toUpperCase());
                      if (idx !== -1) dates.push(new Date(parseInt(formData.p2_birthYear, 10), idx, parseInt(formData.p2_birthDay, 10)));
                    }
                    if (dates.length > 0) {
                      return new Date(Math.min(...dates.map(d => d.getTime())));
                    }
                    return undefined;
                  })()}
                  maxDate={(() => {
                    const dates: Date[] = [];
                    if (!formData.hideDeathDate && formData.deathMonth !== 'MONTH' && formData.deathDay !== 'DD' && formData.deathYear !== 'YYYY') {
                      const idx = MONTHS.indexOf(formData.deathMonth.toUpperCase());
                      if (idx !== -1) dates.push(new Date(parseInt(formData.deathYear, 10), idx, parseInt(formData.deathDay, 10)));
                    }
                    if (!formData.p2_hideDeathDate && formData.p2_deathMonth !== 'MONTH' && formData.p2_deathDay !== 'DD' && formData.p2_deathYear !== 'YYYY') {
                      const idx = MONTHS.indexOf(formData.p2_deathMonth.toUpperCase());
                      if (idx !== -1) dates.push(new Date(parseInt(formData.p2_deathYear, 10), idx, parseInt(formData.p2_deathDay, 10)));
                    }
                    if (dates.length > 0) {
                      return new Date(Math.max(...dates.map(d => d.getTime())));
                    }
                    return new Date();
                  })()}
                  onChange={(m, d, y) => {
                    handleInputChange({
                      weddingMonth: m,
                      weddingDay: d,
                      weddingYear: y
                    });
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <input
                  type="checkbox"
                  id="hideWeddingDate"
                  checked={formData.hideWeddingDate}
                  onChange={(e) => handleInputChange('hideWeddingDate', e.target.checked)}
                  style={{ marginRight: '10px', width: '16px', height: '16px', accentColor: '#5D722C' }}
                />
                <label htmlFor="hideWeddingDate" style={{ fontSize: '12px', color: '#495057', cursor: 'pointer', fontWeight: '500' }}>
                  I don't want wedding date
                </label>
              </div>
            </div>
          )}
        </>
      )}

      {/* Verse Section */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Verse</label>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => handleCategoryChange(e.target.value)}
          disabled={formData.hideVerse || isLoading}
          style={{
            width: '100%',
            padding: '8px 16px',
            border: '2px solid #e1e5e9',
            borderRadius: '5px',
            fontSize: '12px',
            background: formData.hideVerse ? '#ecf0f1' : '#fafbfc',
            cursor: formData.hideVerse || isLoading ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            outline: 'none',
            marginBottom: '10px',
            opacity: formData.hideVerse ? 0.5 : 1
          }}
        >
          {categories.map(category => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        {/* Verse Selector */}
        {selectedCategory !== 'Custom' && (
          <select
            value={formData.selectedVerse}
            onChange={(e) => handleInputChange('selectedVerse', e.target.value)}
            disabled={formData.hideVerse || isLoading}
            style={{
              width: '100%',
              padding: '8px 16px',
              border: '2px solid #e1e5e9',
              borderRadius: '5px',
              fontSize: '12px',
              background: formData.hideVerse ? '#ecf0f1' : '#fafbfc',
              cursor: formData.hideVerse || isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              outline: 'none',
              marginBottom: '10px',
              opacity: formData.hideVerse ? 0.5 : 1
            }}
          >
            <option value="">Select a verse or enter custom text</option>
            {verses.map(verse => (
              <option key={verse.id} value={verse.content}>
                {verse.content}
              </option>
            ))}
          </select>
        )}

        {selectedCategory === 'Custom' && (
          <textarea
            value={formData.customVerse}
            onChange={(e) => handleInputChange('customVerse', e.target.value)}
            placeholder="Enter your custom verse"
            disabled={formData.hideVerse}
            style={{
              width: '100%',
              padding: '8px 16px',
              border: '2px solid #e1e5e9',
              borderRadius: '5px',
              fontSize: '12px',
              minHeight: '100px',
              resize: 'vertical',
              fontFamily: 'inherit',
              fontWeight: '500',
              background: formData.hideVerse ? '#ecf0f1' : '#fafbfc',
              transition: 'all 0.3s ease',
              marginBottom: '10px',
              marginTop: '10px',
              outline: 'none',
              opacity: formData.hideVerse ? 0.5 : 1
            }}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
          <input
            type="checkbox"
            id="hideVerse"
            checked={formData.hideVerse}
            onChange={(e) => handleInputChange('hideVerse', e.target.checked)}
            style={{ marginRight: '10px', width: '16px', height: '16px', accentColor: '#3498db' }}
          />
          <label htmlFor="hideVerse" style={{ fontSize: '12px', color: '#495057', cursor: 'pointer', fontWeight: '500' }}>
            I don't want verse
          </label>
        </div>
      </div>

      {/* Additional Text */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#34495e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Additional Text</label>
        </div>

        {Array.from({ length: visibleAdditionalCount }).map((_, idx) => {
          const i = idx + 1;
          const fieldName = `additionalText${i}`;
          return (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px' }}>
              <textarea
                ref={el => additionalTextRefs.current[i] = el}
                value={formData[fieldName as keyof typeof formData] || ''}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                placeholder={`Enter additional text ${i}`}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '5px',
                  fontSize: '12px',
                  minHeight: '60px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  fontWeight: '500',
                  background: '#fafbfc',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#5D722C'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              />
              <button
                onClick={() => handleRemoveAdditionalText(i)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '4px',
                  borderRadius: '4px',
                  transition: 'background 0.2s'
                }}
                title="Remove text"
                onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}

        {visibleAdditionalCount < 8 && (
          <button
            onClick={() => {
              const nextIdx = visibleAdditionalCount + 1;
              setVisibleAdditionalCount(nextIdx);
              setFocusNewIndex(nextIdx);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#5D722C',
              background: '#eef2e6',
              border: '1px solid #5D722C',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              marginTop: '4px'
            }}
          >
            <span>+</span> Additional Text
          </button>
        )}
      </div>


      {error && (
        <div style={{ color: '#b91c1c', marginTop: 12, fontSize: 13 }} role="alert">
          {error}
        </div>
      )}

      {showExtractionModal && (
        <OrderExtract
          onApply={handleApplyExtractedData}
          onClose={() => setShowExtractionModal(false)}
        />
      )}
    </FormScrollContainer>

  );
});

const Text: React.FC<TextProps> = ({ onClose, onConfirm, isTemplateSelected, selectedColorIndex, colors, preloadedVerses, preloadedCategories }) => {
  const { updateDesign, currentDesign, activeSide, sharedFonts, sharedColors, textStyles, isLoadingTextStyles, pixelsPerInch, renderedTemplateOrigin, openModal } = useDesignContext();

  const isInternalChange = useRef(false);
  const isHydratingFromCanvasRef = useRef(false);
  const renderedTemplateOriginRef = useRef(renderedTemplateOrigin);
  useEffect(() => {
    renderedTemplateOriginRef.current = renderedTemplateOrigin;
  }, [renderedTemplateOrigin]);
  const initialElements = useRef<any[]>(currentDesign?.elements || []);

  const selectedSizeName = (currentDesign as any)?.selectedSizeName || '';
  const isDoubleModelSize = selectedSizeName.includes('36x6x24') || selectedSizeName.includes('48x6x24') || selectedSizeName.includes('54x8x28') || selectedSizeName.toLowerCase().includes('double');

  const [personalDetailsConfirmed, setPersonalDetailsConfirmed] = useState(false);

  const getStorageKey = useCallback(() => {
    const designId = (currentDesign as any)?.id || 'unsaved';
    const side = activeSide || 'front';
    return `design_tool.personal_form_${designId}_${side}`;
  }, [currentDesign, activeSide]);

  const [personalFormData, setPersonalFormData] = useState<any>(() => {
    try {
      const designId = (currentDesign as any)?.id || 'unsaved';
      const side = activeSide || 'front';
      const key = `design_tool.personal_form_${designId}_${side}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const cached = JSON.parse(raw);
        const activeColorId = (currentDesign as any)?.selectedColorId;
        if (cached.cachedSizeName !== selectedSizeName || cached.cachedColorId !== activeColorId) {
          if (!cached.weddingDate && !cached.weddingYear && !cached.p2_firstName && !cached.p2_birthYear) {
            cached.useDoublePerson = isDoubleModelSize;
          }
        } else if (cached.useDoublePerson === undefined) {
          cached.useDoublePerson = isDoubleModelSize;
        }
        cached.cachedColorId = activeColorId;
        cached.cachedSizeName = selectedSizeName;
        return cached;
      }
    } catch { }

    if (currentDesign && currentDesign.elements) {
      const els = currentDesign.elements;
      const side = activeSide || 'front';
      const getText = (id: string) => {
        const targetId = side === 'front' ? id : `${id}-back`;
        return els.find((e: any) => e.type === 'text' && (e.id === targetId || e.id === id || e.id.startsWith(id)) && (e.side || 'front') === side)?.properties?.content || '';
      };

      const first = getText('firstName');
      const middle = getText('middleName');
      const last = getText('lastName');
      const dob = getText('dob');
      const dod = getText('dod');
      const verseContent = getText('verse');
      const weddingDateContent = getText('weddingDate');
      const additionalText = getText('additionalText');
      const hasUserImg = els.some((el: any) => el.type === 'artwork' && el.properties && (el.properties.userUploaded || el.properties.role === 'work' || el.properties.role === 'user'));

      const p2_first = getText('p2_firstName');
      const p2_middle = getText('p2_middleName');
      const p2_dob_val = getText('p2_dob');
      const p2_dod_val = getText('p2_dod');

      if (first || last || dob || dod || p2_first || verseContent || weddingDateContent || hasUserImg) {
        const parseD = (str: string) => {
          if (!str) return { m: 'MONTH', d: 'DD', y: 'YYYY' };
          const s = str.replace(/[.,]/g, ' ').toUpperCase();
          const parts = s.split(/\s+/).filter(Boolean);
          if (parts.length < 3) return { m: 'MONTH', d: 'DD', y: 'YYYY' };
          const foundM = MONTHS.find(m => m.startsWith(parts[0]));
          return { m: foundM || 'MONTH', d: parts[1], y: parts[2] };
        };

        const dobObj = parseD(dob);
        const dodObj = parseD(dod);
        const p2_dobObj = parseD(p2_dob_val);
        const p2_dodObj = parseD(p2_dod_val);

        let sel = '';
        let selCategory = '';
        if (verseContent) {
          sel = 'Custom';
          selCategory = 'Custom';
          if (preloadedVerses) {
            const match = (preloadedVerses || []).find((v: any) => v.content.trim().toLowerCase() === verseContent.trim().toLowerCase());
            if (match) {
              sel = match.content;
              selCategory = match.category || '';
            }
          }
        }

        const isAbbr = (str: string) => {
          if (!str) return false;
          const firstToken = str.split(/\s+/)[0].toUpperCase().replace('.', '');
          if (!firstToken) return false;
          return !MONTHS.includes(firstToken);
        };
        const hasAbbreviation = isAbbr(dob) || isAbbr(dod) || isAbbr(p2_dob_val) || isAbbr(p2_dod_val) || isAbbr(weddingDateContent);

        return {
          firstName: first || '',
          middleName: middle || '',
          lastName: last || '',
          birthMonth: dobObj.m !== 'MONTH' ? dobObj.m : 'JANUARY',
          birthDay: dobObj.d !== 'DD' ? dobObj.d : '1',
          birthYear: dobObj.y !== 'YYYY' ? dobObj.y : '1940',
          deathMonth: dodObj.m,
          deathDay: dodObj.d,
          deathYear: dodObj.y,
          hideDeathDate: !dod,
          p2_firstName: p2_first || '',
          p2_middleName: p2_middle || '',
          p2_birthMonth: p2_dobObj.m !== 'MONTH' ? p2_dobObj.m : 'JANUARY',
          p2_birthDay: p2_dobObj.d !== 'DD' ? p2_dobObj.d : '1',
          p2_birthYear: p2_dobObj.y !== 'YYYY' ? p2_dobObj.y : '1940',
          p2_deathMonth: p2_dodObj.m,
          p2_deathDay: p2_dodObj.d,
          p2_deathYear: p2_dodObj.y,
          p2_hideDeathDate: !p2_dod_val,
          useDoublePerson: (() => {
            if (weddingDateContent || p2_first || p2_dob_val || p2_dod_val) return true;
            try {
              const raw = localStorage.getItem(`design_tool.personal_form_${(currentDesign as any)?.id || 'unsaved'}_${activeSide || 'front'}`);
              if (raw) {
                const cached = JSON.parse(raw);
                if (cached.useDoublePerson !== undefined) {
                  return cached.useDoublePerson;
                }
              }
            } catch { }
            return isDoubleModelSize;
          })(),
          weddingMonth: weddingDateContent ? (parseD(weddingDateContent).m) : 'MONTH',
          weddingDay: weddingDateContent ? (parseD(weddingDateContent).d) : 'DD',
          weddingYear: weddingDateContent ? (parseD(weddingDateContent).y) : 'YYYY',
          hideWeddingDate: !weddingDateContent,
          selectedVerse: sel,
          selectedCategory: selCategory,
          customVerse: verseContent || '',
          hideVerse: false,
          verse: verseContent || '',
          additionalText: additionalText || '',
          additionalText1: getText('additionalText1') || additionalText || '',
          additionalText2: getText('additionalText2') || '',
          additionalText3: getText('additionalText3') || '',
          additionalText4: getText('additionalText4') || '',
          additionalText5: getText('additionalText5') || '',
          additionalText6: getText('additionalText6') || '',
          additionalText7: getText('additionalText7') || '',
          additionalText8: getText('additionalText8') || '',
          abbreviateMonth: hasAbbreviation
        };
      }
    }
    return null;
  });

  useEffect(() => {
    if (openModal !== 'text') {
      return;
    }
    if (isInternalChange.current) {
      return;
    }

    const side = activeSide || 'front';

    if (currentDesign && currentDesign.elements) {
      const els = currentDesign.elements;
      const getText = (id: string) => {
        const targetId = side === 'front' ? id : `${id}-back`;
        return els.find((e: any) => e.type === 'text' && (e.id === targetId || e.id === id || e.id.startsWith(id)) && (e.side || 'front') === side)?.properties?.content || '';
      };

      const first = getText('firstName');
      const middle = getText('middleName');
      const last = getText('lastName');
      const dob = getText('dob');
      const dod = getText('dod');
      const verseContent = getText('verse');
      const weddingDateContent = getText('weddingDate');
      const additionalText = getText('additionalText');
      const hasUserImg = els.some((el: any) => el.type === 'artwork' && el.properties && (el.properties.userUploaded || el.properties.role === 'work' || el.properties.role === 'user'));

      const p2_first = getText('p2_firstName');
      const p2_middle = getText('p2_middleName');
      const p2_dob_val = getText('p2_dob');
      const p2_dod_val = getText('p2_dod');

      if (first || last || dob || dod || p2_first || verseContent || weddingDateContent || hasUserImg) {
        const parseD = (str: string) => {
          if (!str || str.toLowerCase().includes('undefined')) return { m: 'MONTH', d: 'DD', y: 'YYYY' };
          const typed = parseTypedDate(str);
          if (typed) {
            return { m: typed.month, d: typed.day, y: typed.year };
          }
          const s = str.replace(/[.,\/]/g, ' ').toUpperCase();
          const parts = s.split(/\s+/).filter(Boolean);
          if (parts.length < 3) return { m: 'MONTH', d: 'DD', y: 'YYYY' };
          const foundM = MONTHS.find(m => parts[0].includes(m) || m.includes(parts[0]));
          return { m: foundM || 'MONTH', d: parts[1], y: parts[2] };
        };

        const dobObj = parseD(dob);
        const dodObj = parseD(dod);
        const p2_dobObj = parseD(p2_dob_val);
        const p2_dodObj = parseD(p2_dod_val);
        const weddingObj = parseD(weddingDateContent);

        let sel = '';
        let selCategory = '';
        if (verseContent) {
          sel = 'Custom';
          selCategory = 'Custom';
          if (preloadedVerses) {
            const match = (preloadedVerses || []).find((v: any) => v.content.trim().toLowerCase() === verseContent.trim().toLowerCase());
            if (match) {
              sel = match.content;
              selCategory = match.category || '';
            }
          }
        }

        const isAbbr = (str: string) => {
          if (!str) return false;
          const firstToken = str.split(/\s+/)[0].toUpperCase().replace('.', '');
          if (!firstToken) return false;
          return !MONTHS.includes(firstToken);
        };
        const hasAbbreviation = isAbbr(dob) || isAbbr(dod) || isAbbr(p2_dob_val) || isAbbr(p2_dod_val) || isAbbr(weddingDateContent);

        const newData: any = {
          firstName: first || '',
          middleName: middle || '',
          lastName: last || '',
          birthMonth: dobObj.m,
          birthDay: dobObj.d,
          birthYear: dobObj.y,
          deathMonth: dodObj.m,
          deathDay: dodObj.d,
          deathYear: dodObj.y,
          hideDeathDate: !dod,
          p2_firstName: p2_first || '',
          p2_middleName: p2_middle || '',
          p2_birthMonth: p2_dobObj.m,
          p2_birthDay: p2_dobObj.d,
          p2_birthYear: p2_dobObj.y,
          p2_deathMonth: p2_dodObj.m,
          p2_deathDay: p2_dodObj.d,
          p2_deathYear: p2_dodObj.y,
          p2_hideDeathDate: !p2_dod_val,
          useDoublePerson: (() => {
            if (weddingDateContent || p2_first || p2_dob_val || p2_dod_val) return true;
            try {
              const raw = localStorage.getItem(`design_tool.personal_form_${(currentDesign as any)?.id || 'unsaved'}_${activeSide || 'front'}`);
              if (raw) {
                const cached = JSON.parse(raw);
                if (cached.useDoublePerson !== undefined) {
                  return cached.useDoublePerson;
                }
              }
            } catch { }
            return isDoubleModelSize;
          })(),
          weddingMonth: weddingDateContent ? (weddingObj.m) : 'MONTH',
          weddingDay: weddingDateContent ? (weddingObj.d) : 'DD',
          weddingYear: weddingDateContent ? (weddingObj.y) : 'YYYY',
          weddingDate: weddingDateContent || '',
          hideWeddingDate: !weddingDateContent,
          selectedVerse: sel,
          selectedCategory: selCategory,
          customVerse: verseContent || '',
          hideVerse: false,
          verse: verseContent || '',
          additionalText: additionalText || '',
          additionalText1: getText('additionalText1') || additionalText || '',
          additionalText2: getText('additionalText2') || '',
          additionalText3: getText('additionalText3') || '',
          additionalText4: getText('additionalText4') || '',
          additionalText5: getText('additionalText5') || '',
          additionalText6: getText('additionalText6') || '',
          additionalText7: getText('additionalText7') || '',
          additionalText8: getText('additionalText8') || '',
          abbreviateMonth: hasAbbreviation
        };

        isHydratingFromCanvasRef.current = true;
        setPersonalFormData(newData);
        try {
          const cacheData: any = { ...newData };
          cacheData.cachedSizeName = (currentDesign as any)?.selectedSizeName || '';
          cacheData.cachedColorId = (currentDesign as any)?.selectedColorId;
          localStorage.setItem(getStorageKey(), JSON.stringify(cacheData));
        } catch { }
        return;
      }
    }

    const key = getStorageKey();
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const cached = JSON.parse(raw);
        const activeColorId = (currentDesign as any)?.selectedColorId;
        const sizeName = (currentDesign as any)?.selectedSizeName || '';
        if (cached.cachedSizeName !== sizeName || cached.cachedColorId !== activeColorId) {
          cached.useDoublePerson = isDoubleModelSize;
        } else if (cached.useDoublePerson === undefined) {
          cached.useDoublePerson = isDoubleModelSize;
        }
        setPersonalFormData({ ...cached });
        return;
      }
    } catch { }

    setPersonalFormData(null);
  }, [currentDesign.elements, getStorageKey, isDoubleModelSize, preloadedVerses, openModal]);

  const upsertTextElement = (
    elements: any[],
    {
      id: baseId, content, x, y, fontSize,
      align = 'center',
      fontFamily = '',
      color = '',
      boxWidth = 0,
      forcePosition = false,
      forceSize = false,
      forceUpperCase = true,
      forceFont = false,
      lineHeight,
      letterSpacing
    }: {
      id: string;
      content: string;
      x: number;
      y: number;
      fontSize: number;
      align?: 'left' | 'center' | 'right';
      fontFamily?: string;
      color?: string;
      boxWidth?: number;
      forcePosition?: boolean;
      forceSize?: boolean;
      forceUpperCase?: boolean;
      forceFont?: boolean;
      lineHeight?: number;
      letterSpacing?: number;
    }
  ) => {
    const side = activeSide || 'front';
    const targetId = side === 'front' ? baseId : `${baseId}-back`;

    const idx = elements.findIndex(el => el.id === targetId);
    const existing = idx >= 0 ? elements[idx] : null;

    const isManualPos = !!existing?.properties?.isManualPosition;
    const isManualSize = !!existing?.properties?.isManualSize;
    const isManualFont = !!existing?.properties?.isManualFont;
    const isManualAlign = !!existing?.properties?.isManualAlign;

    const contentToSet = forceUpperCase ? (content || '').toUpperCase() : (content || '');
    const contentSame = !!existing && existing?.properties?.content === contentToSet;

    const actualFont = (existing?.properties?.fontFamily && isManualFont && !forceFont)
      ? existing.properties.fontFamily
      : fontFamily;

    // If user has manually sized it, preserve that size unless it's a full repositioning pass
    const actualFontSize = (existing?.properties?.fontSize && isManualSize && !forcePosition)
      ? existing.properties.fontSize
      : fontSize;

    const actualBoxWidth = (existing?.boxWidth && isManualSize && !forcePosition)
      ? existing.boxWidth
      : (boxWidth || existing?.width || 200);

    const actualX = (typeof existing?.x === 'number' && isManualPos && !forcePosition)
      ? existing.x
      : x;

    const actualY = (typeof existing?.y === 'number' && isManualPos && !forcePosition)
      ? existing.y
      : y;

    const textColor = color || existing?.properties?.color || '#ffffff';
    // Respect manual alignment
    const textAlign = (existing?.properties?.align && isManualAlign && !forcePosition)
      ? existing.properties.align
      : (align || 'center');

    const nextProps = {
      ...(existing?.properties || {}),
      content: contentToSet,
      fontSize: actualFontSize,
      fontFamily: actualFont,
      color: textColor,
      align: textAlign,
      _placedBySync: true,
      isManualPosition: isManualPos,
      isManualSize,
      isManualFont,
      isManualAlign,
      ...(lineHeight !== undefined ? { lineHeight } : {}),
      ...(letterSpacing !== undefined ? { letterSpacing } : {})
    };

    if (idx >= 0) {
      elements[idx] = {
        ...existing,
        id: targetId,
        side,
        type: 'text',
        x: Math.round(actualX),
        y: Math.round(actualY),
        width: Math.round(actualBoxWidth),
        boxWidth: Math.round(actualBoxWidth),
        height: existing?.height || 60,
        rotation: existing?.rotation || 0,
        flipX: existing?.flipX,
        flipY: existing?.flipY,
        properties: nextProps
      } as any;
    } else {
      const newEl: any = {
        id: targetId,
        side,
        type: 'text',
        x: Math.round(x),
        y: Math.round(y),
        width: Math.max(Math.round(boxWidth || 200), 50),
        boxWidth: Math.max(Math.round(boxWidth || 200), 50),
        height: 60,
        rotation: 0,
        properties: {
          ...nextProps,
          isManualPosition: false,
          isManualSize: false,
          isManualFont: false
        }
      };
      elements.push(newEl);
    }
    return actualFontSize;
  };

  const estimateTextWidth = useCallback((text: string, fontSize: number, wrapWidth?: number) => {
    if (!text) return 100;
    // Increase factor from 0.7 to 1.1 and padding from 60 to 100 to be very safe against auto-wrapping
    const estimated = Math.max(50, Math.ceil(text.length * fontSize * 1.1) + 100);

    return wrapWidth ? Math.min(estimated, wrapWidth) : estimated;
  }, []);

  const syncTimerRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (openModal !== 'text') {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    }
  }, [openModal]);

  const syncEditorFromForm = useCallback((state: any, options?: { force?: boolean; reposition?: boolean }) => {
    if (openModal !== 'text') {
      return;
    }
    try {
    } catch { }
    const side = activeSide || 'front';
    const getSideId = (id: string) => side === 'front' ? id : `${id}-back`;

    if (!textStyles || isLoadingTextStyles) return;

    isInternalChange.current = true;

    try {
      const cacheState: any = { ...state };
      FONT_CACHE_KEYS.forEach(k => { delete cacheState[k]; });
      cacheState.cachedSizeName = (currentDesign as any)?.selectedSizeName || '';
      cacheState.cachedColorId = (currentDesign as any)?.selectedColorId;
      localStorage.setItem(getStorageKey(), JSON.stringify(cacheState));
    } catch { }

    const activeColorId = (currentDesign as any)?.selectedColorId;
    const activeColorObj = sharedColors.find((c: any) => Number(c.id) === Number(activeColorId))
      || colors.find((c: any) => Number(c.id) === Number(activeColorId))
      || {};
    const isPJB = (activeColorObj as any)?.code === 'PJB'
      || String((activeColorObj as any)?.name || '').toUpperCase().includes('BLACK');

    const textColor = isPJB ? '#FFFFFF' : '#000000';

    const FIELD_TO_FRONT_ID: Record<string, string> = {
      first_name: 'firstName', middle_name: 'middleName', last_name: 'lastName',
      date: 'dob', verse: 'verse', additional_text: 'additionalText',
    };

    const getFont = (fieldType: string): string => {
      const family = textStyles?.[fieldType]?.font_family;
      if (family) return family;
      if (fieldType === 'verse' || fieldType === 'additional_text') return 'MonotypeCorsiva';
      return '';
    };

    const nameFont = getFont('first_name');
    const dateFont = getFont('date');
    const verseFont = getFont('verse');
    const additionalTextFont = getFont('additional_text');

    const isGrey = (activeColorObj as any)?.code === 'MG'
      || (activeColorObj as any)?.code === 'Grey'
      || String((activeColorObj as any)?.name || '').toUpperCase().includes('GRAY')
      || String((activeColorObj as any)?.name || '').toUpperCase().includes('GREY');

    const weddingFont = isGrey ? 'Vermarco' : 'PamegaScript';

    requestAnimationFrame(() => {
      updateDesign(prev => {
        let newElements = [...prev.elements];

        function getActualWidth(id: string, content: string, fontSize: number, defaultW: number) {
          const targetId = getSideId(id);
          const el = newElements.find(x => x.id === targetId);
          if (el && el.properties?.isManualSize && el.width) {
            return el.width;
          }
          return Math.min(defaultW, estimateTextWidth(content, fontSize));
        }

        const candidates = newElements.filter(el => {
          const p = el.properties || {};
          return (el.type === 'artwork' && (p.role === 'template' || p.isTemplate)) || el.type === 'tablet' || el.type === 'base';
        });

        const applyForcePosition = options?.reposition === true;

        const rto = renderedTemplateOriginRef.current;
        if (!rto || rto.width <= 0) {
          return prev;
        }

        const rect = {
          x: rto.x,
          y: rto.y,
          width: rto.width,
          height: rto.height,
        };

        const centerX = rect.x + rect.width / 2;
        const centerY = rect.y + rect.height / 2;
        const fullWidth = Math.max(200, Math.floor(rect.width * 0.9));
        const monumentWidth = rect.width;

        const DPI = pixelsPerInch || 96;
        const inchesToPixels = (inches: number) => inches * DPI;

        const getSizeConstraints = (fieldType: string): { min: number; max: number } | null => {
          if (fieldType === 'wedding') {
            return {
              min: Math.floor(inchesToPixels(0.875)),
              max: Math.ceil(inchesToPixels(0.875))
            };
          }
          if (textStyles?.[fieldType]) {
            return {
              min: fieldType === 'verse'
                ? Math.floor(inchesToPixels(Math.min(textStyles[fieldType].min_size_inches, 0.875)))
                : Math.floor(inchesToPixels(textStyles[fieldType].min_size_inches)),
              max: Math.ceil(inchesToPixels(textStyles[fieldType].max_size_inches))
            };
          }
          return null;
        };

        const baseSizes = {
          first: inchesToPixels(textStyles.first_name.min_size_inches),
          middle: inchesToPixels(textStyles.middle_name.min_size_inches),
          last: inchesToPixels(textStyles.last_name.min_size_inches),
          date: inchesToPixels(textStyles.date.min_size_inches),
          wedding: inchesToPixels(0.875),
          verse: inchesToPixels(0.875),
          additional: inchesToPixels(textStyles.additional_text.min_size_inches),
        };
        const baseGap = 12;

        let verse = '';
        if (!state.hideVerse) {
          const isPresetVerseSelected = state.selectedVerse && state.selectedVerse !== 'Custom' && state.selectedVerse !== 'Select a verse or enter custom text';
          const isCustomVerseSelected = (state.selectedCategory === 'Custom' || state.selectedVerse === 'Custom') && !isPresetVerseSelected;

          if (isPresetVerseSelected) {
            verse = state.selectedVerse;
          } else if (isCustomVerseSelected) {
            verse = state.customVerse || '';
          }
        }

        const formatMonth = (month: string): string => {
          if (!month || month === 'MONTH') return month || 'MONTH';
          const m = month.toUpperCase();
          if (state.abbreviateMonth === false) return m;
          if (m === 'MAY' || m === 'JUNE' || m === 'JULY') return m;
          if (m === 'SEPTEMBER') return 'SEPT.';
          return m.slice(0, 3) + '.';
        };

        let estimatedTotalHeight = 0;
        let isDouble = !!state.useDoublePerson;

        const dob1 = state.yearOnly
          ? (state.birthYear && state.birthYear !== 'YYYY' ? state.birthYear : '')
          : ((state.birthMonth !== 'MONTH' && state.birthDay !== 'DD' && state.birthYear !== 'YYYY') ? `${formatMonth(state.birthMonth)} ${state.birthDay}, ${state.birthYear}` : '');
        const dod1 = state.yearOnly
          ? (!state.hideDeathDate && state.deathYear && state.deathYear !== 'YYYY' ? state.deathYear : '')
          : ((!state.hideDeathDate && state.deathMonth !== 'MONTH' && state.deathDay !== 'DD' && state.deathYear !== 'YYYY') ? `${formatMonth(state.deathMonth)} ${state.deathDay}, ${state.deathYear}` : '');
        const dob2 = state.yearOnly
          ? (state.p2_birthYear && state.p2_birthYear !== 'YYYY' ? state.p2_birthYear : '')
          : ((state.p2_birthMonth !== 'MONTH' && state.p2_birthDay !== 'DD' && state.p2_birthYear !== 'YYYY') ? `${formatMonth(state.p2_birthMonth)} ${state.p2_birthDay}, ${state.p2_birthYear}` : '');
        const dod2 = state.yearOnly
          ? (!state.p2_hideDeathDate && state.p2_deathYear && state.p2_deathYear !== 'YYYY' ? state.p2_deathYear : '')
          : ((!state.p2_hideDeathDate && state.p2_deathMonth !== 'MONTH' && state.p2_deathDay !== 'DD' && state.p2_deathYear !== 'YYYY') ? `${formatMonth(state.p2_deathMonth)} ${state.p2_deathDay}, ${state.p2_deathYear}` : '');
        const weddingDate = state.yearOnly
          ? (!state.hideWeddingDate && state.weddingYear && state.weddingYear !== 'YYYY' ? state.weddingYear : (!state.hideWeddingDate && state.weddingDate ? state.weddingDate : ''))
          : (!state.hideWeddingDate
              ? (
                  (!!state.weddingMonth && state.weddingMonth !== 'MONTH' &&
                   !!state.weddingDay && state.weddingDay !== 'DD' &&
                   !!state.weddingYear && state.weddingYear !== 'YYYY')
                    ? `${formatMonth(state.weddingMonth)} ${state.weddingDay}, ${state.weddingYear}`
                    : (state.weddingDate || '')
                )
              : '');

        const countLinesP1 = (state.firstName ? 1 : 0) + (state.middleName?.trim() ? 1 : 0) + (dob1 ? 1 : 0) + (dod1 ? 1 : 0);
        const countLinesP2 = (state.p2_firstName ? 1 : 0) + (state.p2_middleName?.trim() ? 1 : 0) + (dob2 ? 1 : 0) + (dod2 ? 1 : 0);
        const maxPersonLines = Math.max(countLinesP1, countLinesP2);

        const addTextList: { id: string; val: string }[] = [];
        for (let i = 1; i <= 5; i++) {
          const val = state[`additionalText${i}`] as string;
          if (val) addTextList.push({ id: `additionalText${i}`, val: val });
        }
        if (addTextList.length === 0 && state.additionalText) addTextList.push({ id: 'additionalText', val: state.additionalText });

        if (isDouble) {
          const personH = maxPersonLines * baseSizes.first + (maxPersonLines > 0 ? maxPersonLines - 1 : 0) * baseGap;
          const sharedH = (state.lastName ? baseSizes.last + baseGap * 2 : 0) + (verse ? baseSizes.verse + baseGap : 0) + (weddingDate ? baseSizes.date + baseGap : 0) + (addTextList.length * (baseSizes.verse + baseGap));
          estimatedTotalHeight = personH + sharedH + baseGap;
        } else {
          const lines = [];
          if (state.firstName) lines.push(baseSizes.first);
          if (state.middleName?.trim()) lines.push(baseSizes.first);
          if (state.lastName) lines.push(baseSizes.last);
          if (dob1) lines.push(baseSizes.date);
          if (dod1) lines.push(baseSizes.date);
          if (verse) lines.push(baseSizes.verse);
          addTextList.forEach(() => lines.push(baseSizes.additional));
          estimatedTotalHeight = lines.reduce((a, b) => a + b, 0) + (lines.length > 0 ? lines.length - 1 : 0) * baseGap;
        }

        const safeZoneHeight = (rect.height || 600) * 0.95;
        const scaleFactor = estimatedTotalHeight > safeZoneHeight ? safeZoneHeight / estimatedTotalHeight : 1;

        const snapAndConstrain = (sizePixels: number, fieldType: string, incrementInches: number): number => {
          const constraints = getSizeConstraints(fieldType);
          const clamped = constraints
            ? Math.max(constraints.min, Math.min(sizePixels, constraints.max))
            : sizePixels;
          const snappedInches = Math.round((clamped / DPI) / incrementInches) * incrementInches;
          const snappedPx = Math.max(incrementInches * DPI, snappedInches * DPI);
          return constraints
            ? Math.max(constraints.min, Math.min(snappedPx, constraints.max))
            : snappedPx;
        };

        const sizes = {
          first: snapAndConstrain(baseSizes.first * scaleFactor, 'first_name', 0.25),
          middle: snapAndConstrain(baseSizes.middle * scaleFactor, 'middle_name', 0.25),
          last: snapAndConstrain(baseSizes.last * scaleFactor, 'last_name', 0.25),
          date: snapAndConstrain(baseSizes.date * scaleFactor, 'date', 0.25),
          wedding: snapAndConstrain(baseSizes.wedding * scaleFactor, 'wedding', 0.125),
          verse: snapAndConstrain(baseSizes.verse * scaleFactor, 'verse', 0.125),
          additional: snapAndConstrain(baseSizes.additional * scaleFactor, 'additional_text', 0.25)
        };
        const gap = Math.floor(baseGap * scaleFactor);

        if (state.useDoublePerson && side === 'front') {
          const colWidth = Math.floor(fullWidth * 0.45);
          const leftX = Math.floor(centerX - fullWidth / 2);
          const rightX = Math.floor(centerX + fullWidth / 2 - colWidth);

          // Phase 1: Determine stack height using actual element sizes (respecting manual adjustments)
          const getTextSize = (id: string, defaultSize: number) => {
            const targetId = getSideId(id);
            const el = newElements.find(x => x.id === targetId);
            if (el && el.properties?.isManualSize && el.properties?.fontSize) {
              return el.properties.fontSize;
            }
            return defaultSize;
          };

          const sFirst = getTextSize('firstName', sizes.first);
          const sFirst2 = getTextSize('p2_firstName', sizes.first);
          const sMiddle = getTextSize('middleName', sizes.middle);
          const sMiddle2 = getTextSize('p2_middleName', sizes.middle);
          const sLast = getTextSize('lastName', sizes.last);
          const sDob = getTextSize('dob', sizes.date);
          const sDob2 = getTextSize('p2_dob', sizes.date);
          const sDod = getTextSize('dod', sizes.date);
          const sDod2 = getTextSize('p2_dod', sizes.date);
          const sVerse = getTextSize('verse', sizes.verse);
          const sWedding = getTextSize('weddingDate', sizes.wedding);

          const getLinesCount = (p: number) => {
            let count = 1;
            if ((p === 1 ? state.middleName : state.p2_middleName)?.trim()) count++;
            if (p === 1 ? dob1 : dob2) count++;
            if (p === 1 ? dod1 : dod2) count++;
            return count;
          };

          const h1 = (state.firstName ? sFirst : 0) +
            (state.middleName?.trim() ? sMiddle : 0) +
            (dob1 ? sDob : 0) +
            (dod1 ? sDod : 0) +
            (getLinesCount(1) > 1 ? (getLinesCount(1) - 1) * gap : 0);

          const h2 = (state.p2_firstName ? sFirst2 : 0) +
            (state.p2_middleName?.trim() ? sMiddle2 : 0) +
            (dob2 ? sDob2 : 0) +
            (dod2 ? sDod2 : 0) +
            (getLinesCount(2) > 1 ? (getLinesCount(2) - 1) * gap : 0);

          const maxPersonH = Math.max(h1, h2);
          const sharedH = (state.lastName && !state.hideLastName ? sLast + gap * 2 : 0) +
            (state.verse ? sVerse + gap : 0) +
            (weddingDate ? sWedding + gap : 0);

          let addTextHeight = 0;
          addTextList.forEach((item) => {
            addTextHeight += getTextSize(item.id, sizes.additional) + gap;
          });

          const totalH = sharedH + addTextHeight + maxPersonH + gap;
          let cursorY = Math.floor(centerY - totalH / 2);

          if (state.lastName && !state.hideLastName) {
            const w = getActualWidth('lastName', state.lastName, sLast, fullWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'lastName', content: state.lastName, x, y: cursorY, fontSize: sLast, fontFamily: nameFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            cursorY += usedH + gap * 2;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('lastName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          const personStartY = cursorY;
          const c1Center = leftX + colWidth / 2;
          let curr1 = personStartY;

          if (state.firstName) {
            const w = getActualWidth('firstName', state.firstName, sFirst, colWidth);
            const x = Math.floor(c1Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'firstName', content: state.firstName, x, y: curr1, fontSize: sFirst, fontFamily: nameFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            curr1 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('firstName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (state.middleName?.trim()) {
            const w = getActualWidth('middleName', state.middleName, sMiddle, colWidth);
            const x = Math.floor(c1Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'middleName', content: state.middleName, x, y: curr1, fontSize: sMiddle, fontFamily: nameFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            curr1 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('middleName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }
          if (dob1) {
            const w = getActualWidth('dob', dob1, sDob, colWidth);
            const x = Math.floor(c1Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'dob', content: dob1, x, y: curr1, fontSize: sDob, fontFamily: dateFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.2, letterSpacing: 2 });
            curr1 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('dob'));
            if (idx >= 0) newElements.splice(idx, 1);
          }
          if (dod1) {
            const w = getActualWidth('dod', dod1, sDod, colWidth);
            const x = Math.floor(c1Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'dod', content: dod1, x, y: curr1, fontSize: sDod, fontFamily: dateFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.2, letterSpacing: 2 });
            curr1 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('dod'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          const c2Center = rightX + colWidth / 2;
          let curr2 = personStartY;

          if (state.p2_firstName) {
            const w = getActualWidth('p2_firstName', state.p2_firstName, sFirst2, colWidth);
            const x = Math.floor(c2Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'p2_firstName', content: state.p2_firstName, x, y: curr2, fontSize: sFirst2, fontFamily: nameFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            curr2 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('p2_firstName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }
          if (state.p2_middleName?.trim()) {
            const w = getActualWidth('p2_middleName', state.p2_middleName, sMiddle2, colWidth);
            const x = Math.floor(c2Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'p2_middleName', content: state.p2_middleName, x, y: curr2, fontSize: sMiddle2, fontFamily: nameFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            curr2 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('p2_middleName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }
          if (dob2) {
            const w = getActualWidth('p2_dob', dob2, sDob2, colWidth);
            const x = Math.floor(c2Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'p2_dob', content: dob2, x, y: curr2, fontSize: sDob2, fontFamily: dateFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.2, letterSpacing: 2 });
            curr2 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('p2_dob'));
            if (idx >= 0) newElements.splice(idx, 1);
          }
          if (dod2) {
            const w = getActualWidth('p2_dod', dod2, sDod2, colWidth);
            const x = Math.floor(c2Center - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'p2_dod', content: dod2, x, y: curr2, fontSize: sDod2, fontFamily: dateFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.2, letterSpacing: 2 });
            curr2 += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('p2_dod'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          cursorY = personStartY + maxPersonH + gap * 2;

          if (weddingDate) {
            const w = getActualWidth('weddingDate', weddingDate, sWedding, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'weddingDate', content: weddingDate, x, y: cursorY, fontSize: sWedding, fontFamily: weddingFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('weddingDate'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (verse) {
            const w = getActualWidth('verse', verse, sVerse, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'verse', content: verse, x, y: cursorY, fontSize: sVerse, fontFamily: verseFont, align: 'center', boxWidth: w, color: textColor, forcePosition: applyForcePosition, forceSize: true, forceFont: false, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('verse'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          addTextList.forEach((item) => {
            const h = getTextSize(item.id, sizes.additional);
            const w = getActualWidth(item.id, item.val, h, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, {
              id: item.id,
              content: item.val,
              x,
              y: cursorY,
              fontSize: h,
              fontFamily: additionalTextFont,
              align: 'center',
              boxWidth: w,
              color: textColor,
              forcePosition: applyForcePosition,
              forceSize: true,
              forceUpperCase: false,
              forceFont: true,
              lineHeight: 1.1,
              letterSpacing: 2
            });
            cursorY += usedH + gap;
          });

          ['additionalText', 'additionalText1', 'additionalText2', 'additionalText3', 'additionalText4', 'additionalText5', 'additionalText6', 'additionalText7', 'additionalText8'].forEach(id => {
            if (!addTextList.find(x => x.id === id)) {
              const idx = newElements.findIndex(el => el.id === getSideId(id));
              if (idx >= 0) newElements.splice(idx, 1);
            }
          });

        } else {
          const colWidth = Math.floor(fullWidth * 0.45);
          const leftX = Math.floor(centerX - fullWidth / 2);
          const rightX = Math.floor(centerX + fullWidth / 2 - colWidth);

          const dob_val = state.yearOnly
            ? (state.birthYear && state.birthYear !== 'YYYY' ? state.birthYear : '')
            : ((state.birthMonth !== 'MONTH' && state.birthDay !== 'DD' && state.birthYear !== 'YYYY') ? `${formatMonth(state.birthMonth)} ${state.birthDay}, ${state.birthYear}` : '');
          let dod_val = state.yearOnly
            ? (!state.hideDeathDate && state.deathYear && state.deathYear !== 'YYYY' ? state.deathYear : '')
            : ((!state.hideDeathDate && state.deathMonth !== 'MONTH' && state.deathDay !== 'DD' && state.deathYear !== 'YYYY') ? `${formatMonth(state.deathMonth)} ${state.deathDay}, ${state.deathYear}` : '');

          const isFront = side === 'front';

          const lines: string[] = [];
          if (isFront && state.firstName) lines.push('first');
          if (isFront && state.middleName?.trim()) lines.push('middle');
          if (state.lastName && !state.hideLastName) lines.push('last');
          if (isFront && dob_val) lines.push('dob');
          if (isFront && dod_val) lines.push('dod');
          if (verse) lines.push('verse');
          if (addTextList.length > 0) lines.push('additionalText');

          // Phase 1: Determine stack height using actual element sizes (respecting manual adjustments)
          const getTextSize = (id: string, defaultSize: number) => {
            const targetId = getSideId(id);
            const el = newElements.find(x => x.id === targetId);
            if (el && el.properties?.isManualSize && el.properties?.fontSize) {
              return el.properties.fontSize;
            }
            return defaultSize;
          };

          const sFirst = (isFront && state.firstName) ? getTextSize('firstName', sizes.first) : 0;
          const sMiddle = (isFront && state.middleName?.trim()) ? getTextSize('middleName', sizes.middle) : 0;
          const sLast = (state.lastName && !state.hideLastName) ? getTextSize('lastName', sizes.last) : 0;
          const sDob = (isFront && dob_val) ? getTextSize('dob', sizes.date) : 0;
          const sDod = (isFront && dod_val) ? getTextSize('dod', sizes.date) : 0;
          const sVerse = (verse) ? getTextSize('verse', sizes.verse) : 0;

          let sumActualH = sFirst + sMiddle + sLast + sDob + sDod + sVerse;
          let countLines = (sFirst > 0 ? 1 : 0) + (sMiddle > 0 ? 1 : 0) + (sLast > 0 ? 1 : 0) + (sDob > 0 ? 1 : 0) + (sDod > 0 ? 1 : 0) + (sVerse > 0 ? 1 : 0);

          addTextList.forEach(at => {
            const h = getTextSize(at.id, sizes.additional);
            sumActualH += h;
            countLines += 1;
          });

          const actualBlockHeight = sumActualH + gap * (countLines - 1);
          let cursorY = Math.floor(centerY - actualBlockHeight / 2);

          if (isFront && state.firstName) {
            const w = getActualWidth('firstName', state.firstName, sFirst, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'firstName', content: state.firstName, x, y: cursorY, fontSize: sFirst, fontFamily: nameFont, align: 'center', color: textColor, boxWidth: w, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('firstName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (isFront && state.middleName?.trim()) {
            const w = getActualWidth('middleName', state.middleName, sMiddle, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'middleName', content: state.middleName, x, y: cursorY, fontSize: sMiddle, fontFamily: nameFont, align: 'center', color: textColor, boxWidth: w, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('middleName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (state.lastName && !state.hideLastName) {
            const w = getActualWidth('lastName', state.lastName, sLast, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'lastName', content: state.lastName, x, y: cursorY, fontSize: sLast, fontFamily: nameFont, align: 'center', color: textColor, boxWidth: w, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('lastName'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (isFront && dob_val) {
            const w = getActualWidth('dob', dob_val, sDob, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'dob', content: dob_val, x, y: cursorY, fontSize: sDob, fontFamily: dateFont, align: 'center', color: textColor, boxWidth: w, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.2, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('dob'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (isFront && dod_val) {
            const w = getActualWidth('dod', dod_val, sDod, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'dod', content: dod_val, x, y: cursorY, fontSize: sDod, fontFamily: dateFont, align: 'center', color: textColor, boxWidth: w, forcePosition: applyForcePosition, forceSize: true, forceFont: true, forceUpperCase: false, lineHeight: 1.2, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('dod'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          if (verse) {
            const w = getActualWidth('verse', verse, sVerse, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, { id: 'verse', content: verse, x, y: cursorY, fontSize: sVerse, fontFamily: verseFont, align: 'center', color: textColor, boxWidth: w, forcePosition: applyForcePosition, forceSize: true, forceFont: false, forceUpperCase: false, lineHeight: 1.1, letterSpacing: 2 });
            cursorY += usedH + gap;
          } else {
            const idx = newElements.findIndex(el => el.id === getSideId('verse'));
            if (idx >= 0) newElements.splice(idx, 1);
          }

          addTextList.forEach((item) => {
            const h = getTextSize(item.id, sizes.additional);
            const w = getActualWidth(item.id, item.val, h, monumentWidth);
            const x = Math.floor(centerX - w / 2);
            const usedH = upsertTextElement(newElements, {
              id: item.id,
              content: item.val,
              x,
              y: cursorY,
              fontSize: h,
              fontFamily: additionalTextFont,
              align: 'center',
              boxWidth: w,
              color: textColor,
              forcePosition: applyForcePosition,
              forceSize: true,
              forceUpperCase: false,
              forceFont: true,
              lineHeight: 1.1,
              letterSpacing: 2
            });
            cursorY += usedH + gap;
          });

          ['additionalText', 'additionalText1', 'additionalText2', 'additionalText3', 'additionalText4', 'additionalText5', 'additionalText6', 'additionalText7', 'additionalText8'].forEach(id => {
            if (!addTextList.find(x => x.id === id)) {
              const idx = newElements.findIndex(el => el.id === getSideId(id));
              if (idx >= 0) newElements.splice(idx, 1);
            }
          });

          // IMPORTANT: Explicitly remove Person 2 elements if Double Person Design is off
          ['p2_firstName', 'p2_middleName', 'p2_dob', 'p2_dod'].forEach(id => {
            const idx = newElements.findIndex(el => el.id === getSideId(id));
            if (idx >= 0) newElements.splice(idx, 1);
          });
        }

        const elementsUnchanged = (newElements.length === prev.elements.length) &&
          newElements.every((el: any, i: number) => el === prev.elements[i]);
        if (elementsUnchanged) return prev;

        return { ...prev, elements: newElements };
      });
      setTimeout(() => {
        isInternalChange.current = false;
      }, 100);
    });
  }, [updateDesign, sharedColors, colors, selectedColorIndex, activeSide, sharedFonts, textStyles, isLoadingTextStyles, pixelsPerInch, openModal]);

  const rtoRef = useRef(renderedTemplateOrigin);
  useEffect(() => { rtoRef.current = renderedTemplateOrigin; }, [renderedTemplateOrigin]);

  const didInitialRepositionRef = useRef<boolean>(false);
  const initialRepositionBoundsKeyRef = useRef<string>('');
  const initialRepositionTimerRef = useRef<any>(null);
  useEffect(() => {
    if (openModal !== 'text') {
      didInitialRepositionRef.current = false;
      initialRepositionBoundsKeyRef.current = '';
      if (initialRepositionTimerRef.current) {
        clearTimeout(initialRepositionTimerRef.current);
        initialRepositionTimerRef.current = null;
      }
      return;
    }

    if (!personalFormData) return;
    if (!textStyles || isLoadingTextStyles) return;

    const rto = renderedTemplateOrigin;
    if (!rto || rto.width <= 0) return;

    const boundsKey = `rto:${rto.x},${rto.y},${rto.width},${rto.height}`;
    if (!didInitialRepositionRef.current && initialRepositionBoundsKeyRef.current !== boundsKey) {
      initialRepositionBoundsKeyRef.current = boundsKey;
    }

    if (didInitialRepositionRef.current) return;

    if (initialRepositionTimerRef.current) {
      clearTimeout(initialRepositionTimerRef.current);
      initialRepositionTimerRef.current = null;
    }

    initialRepositionTimerRef.current = setTimeout(() => {
      if (!isTemplateSelected || !personalFormData || !renderedTemplateOrigin) return;
      if (openModal !== 'text') return;
      const latest = renderedTemplateOriginRef.current;
      if (!latest || latest.width <= 0) return;
      const latestKey = `rto:${latest.x},${latest.y},${latest.width},${latest.height}`;
      if (latestKey !== initialRepositionBoundsKeyRef.current) return;
      didInitialRepositionRef.current = true;
      try {
      } catch { }
      syncEditorFromForm(personalFormData, { force: true, reposition: false });
    }, 80);
  }, [openModal, personalFormData, renderedTemplateOrigin, textStyles, isLoadingTextStyles, syncEditorFromForm]);

  const debouncedSyncFromForm = useCallback((state: any) => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      if (openModal !== 'text') return;

      syncEditorFromForm(state, { force: true, reposition: false });
    }, 200);
  }, [syncEditorFromForm, openModal]);

  const prevColorRef = useRef<number>(Number((currentDesign as any)?.selectedColorId || 0));

  useEffect(() => {
    if (openModal !== 'text') return;
    const activeColorId = Number((currentDesign as any)?.selectedColorId || 0);
    if (prevColorRef.current !== activeColorId && personalFormData) {
      prevColorRef.current = activeColorId;
      syncEditorFromForm(personalFormData, { force: true });
    }
  }, [currentDesign?.selectedColorId, personalFormData, syncEditorFromForm, renderedTemplateOrigin, openModal]);

  const prevActiveSideRef = useRef<string>(activeSide || 'front');
  useEffect(() => {
    if (openModal !== 'text') return;
    if (prevActiveSideRef.current !== activeSide) {
      prevActiveSideRef.current = activeSide || 'front';
      hasSyncedStylesRef.current = false;
      if (personalFormData && textStyles && !isLoadingTextStyles) {
        syncEditorFromForm(personalFormData, { force: true });
      }
    }
  }, [activeSide, personalFormData, textStyles, isLoadingTextStyles, syncEditorFromForm, openModal]);

  const hasSyncedStylesRef = useRef(false);
  const prevTemplateBoundsRef = useRef<string>('');
  useEffect(() => {
    if (openModal !== 'text') return;
    if (textStyles && !isLoadingTextStyles && personalFormData) {
      const templateElForSync = (currentDesign?.elements || []).find((el: any) =>
        el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate)
      ) || (currentDesign?.elements || []).find((el: any) => el.type === 'tablet' || el.type === 'base');

      if (!templateElForSync) {
        prevTemplateBoundsRef.current = '';
        return;
      }

      const rto = renderedTemplateOrigin;
      if (!rto || rto.width <= 0) {
        prevTemplateBoundsRef.current = '';
        return;
      }

      const boundsKey = `rto:${rto.x},${rto.y},${rto.width},${rto.height}`;

      const boundsChanged = prevTemplateBoundsRef.current !== '' && prevTemplateBoundsRef.current !== boundsKey;
      prevTemplateBoundsRef.current = boundsKey;

      if (!hasSyncedStylesRef.current || boundsChanged) {
        hasSyncedStylesRef.current = true;
        syncEditorFromForm(personalFormData, { force: true, reposition: false });
      }
    } else if (isLoadingTextStyles) {
      hasSyncedStylesRef.current = false;
      prevTemplateBoundsRef.current = '';
    }
  }, [textStyles, isLoadingTextStyles, personalFormData, syncEditorFromForm, currentDesign?.elements, renderedTemplateOrigin, openModal]);

  // Note: We removed the immediate sync useEffect that was here.
  // Real-time synchronization is now handled exclusively by debouncedSyncFromForm
  // triggered in the onChange prop of PersonalDetailsForm. This prevents 
  // the editor from "striking" or lagging during text input or date selection.
  // The initial sync on modal open is still handled by initialRepositionTimerRef 
  // and hasSyncedStylesRef above.

  if (!isTemplateSelected) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
        <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>Template Required</h3>
        <p style={{ margin: '0 0 20px 0', fontSize: '12px' }}>
          Please select a template first to enable text customization.
        </p>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            border: '1px solid #e1e5e9',
            borderRadius: '6px',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            color: '#6c757d'
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <PersonalDetailsForm
      key={`${(currentDesign as any)?.id || 'new'}-${selectedSizeName}`}
      initialData={personalFormData}
      selectedColorIndex={selectedColorIndex}
      colors={colors}
      preloadedVerses={preloadedVerses}
      preloadedCategories={preloadedCategories}
      setPersonalFormData={setPersonalFormData}
      onChange={(newData) => {
        setPersonalFormData(newData);
        debouncedSyncFromForm(newData);
      }}
      side={activeSide}
    />
  );
};



export default Text;